<template>
  <div class="invest-page">
    <AppHeader />

    <main class="page-body">
      <section class="hero-premium">
        <div class="hero-grid-bg"></div>
        <div class="hero-glow-gold"></div>
        <div class="hero-glow-blue"></div>
        <div class="hero-scan-beam"></div>
        <div class="hero-data-stream"></div>

        <div class="arbitrage-orbit">
          <div class="orbit-ring ring-a"></div>
          <div class="orbit-ring ring-b"></div>
          <div class="orbit-ring ring-c"></div>
          <div class="orbit-core">₿</div>
          <div class="orbit-dot dot-a"></div>
          <div class="orbit-dot dot-b"></div>
        </div>

        <div class="hero-content">
          <div class="hero-badge">
            <i></i>
            AI MARKET EXECUTION HUB
          </div>

          <div class="hero-system-rail">
            <span class="rail-online">
              <i></i>
              EXECUTION NETWORK ONLINE
            </span>
            <span>BSC SETTLEMENT LAYER</span>
            <span>BTC / USDT</span>
          </div>

          <h1>
            配置策略方案
            <span>启动智能执行与资产管理</span>
          </h1>

          <p class="hero-slogan">
            标准执行策略 · {{ trialConfig.days || 7 }}日策略体验方案 · USDT 统一结算
          </p>

          <p class="hero-desc">
            标准执行策略按照 {{ modelFeePercentText }}% AI 策略服务费与
            {{ poolRatePercentText }}% 有效执行额度进行配置；符合条件的用户可开通固定
            {{ money(trialConfig.amount) }} USDT 的体验策略，完整了解授权、网络确认、策略执行与结算流程。
          </p>

          <div class="hero-model">
            <div>
              <span>有效执行额度</span>
              <strong>{{ poolRatePercentText }}%</strong>
              <small>进入策略执行与结算</small>
            </div>
            <div>
              <span>体验策略额度</span>
              <strong>{{ money(trialConfig.amount) }}</strong>
              <small>USDT / ADDRESS</small>
            </div>
            <div>
              <span>体验策略周期</span>
              <strong>{{ trialConfig.days || 7 }}D</strong>
              <small>到期统一结算</small>
            </div>
          </div>
        </div>
      </section>

      <section v-if="!isLoggedIn" class="section-card system-notice-card">
        <div class="section-head">
          <div>
            <h2>请先连接钱包并登录</h2>
            <p>登录成功后，系统会自动读取策略参数、体验资格和你的真实策略记录。</p>
          </div>
          <span>AUTH REQUIRED</span>
        </div>
      </section>

      <section v-if="isLoggedIn && errorMsg" class="section-card system-notice-card error-card">
        <div class="section-head">
          <div>
            <h2>数据加载失败</h2>
            <p>{{ errorMsg }}</p>
          </div>
          <span>NETWORK</span>
        </div>
        <button type="button" class="retry-btn" @click="loadPageData">重新加载</button>
      </section>

      <section class="section-card deposit-card">
        <div class="section-head">
          <div>
            <h2>策略配置中心</h2>
            <p>选择标准执行策略或体验策略方案，系统将依据当前策略参数完成链上资产配置并生成策略记录。</p>
          </div>
          <span>Strategy Setup</span>
        </div>

        <div class="order-mode-grid">
          <button
            type="button"
            :class="['order-mode-card', 'standard', { active: orderMode === 'standard' }]"
            @click="selectOrderMode('standard')"
            :disabled="orderSubmitting || loadingConfig"
          >
            <span class="mode-check"></span>
            <span class="mode-icon">₿</span>
            <span class="mode-copy">
              <span class="mode-topline">
                <b>标准执行策略</b>
                <em>持续执行</em>
              </span>
              <strong>自定义策略配置额度</strong>
              <small>
                {{ modelFeePercentText }}% AI 策略服务费 ·
                {{ poolRatePercentText }}% 纳入有效执行额度
              </small>
            </span>
          </button>

          <button
            type="button"
            :class="['order-mode-card', 'trial', { active: orderMode === 'trial' }]"
            @click="selectOrderMode('trial')"
            :disabled="orderSubmitting || loadingConfig"
          >
            <span class="mode-check"></span>
            <span class="mode-icon">{{ trialConfig.days || 7 }}D</span>
            <span class="mode-copy">
              <span class="mode-topline">
                <b>{{ trialConfig.days || 7 }}天策略体验方案</b>
                <em>流程体验</em>
              </span>
              <strong>预设配置额度 {{ money(trialConfig.amount) }} USDT</strong>
              <small>固定周期 · 预设结算 · USDT 结算</small>
            </span>
          </button>
        </div>

        <div :class="['selected-plan-bar', orderMode]">
          <div class="selected-plan-left">
            <span class="selected-dot"></span>
            <div>
              <b>{{ orderMode === 'standard' ? '当前选择：标准执行策略' : `当前选择：${trialConfig.days || 7}天策略体验方案` }}</b>
              <p>
                {{
                  orderMode === 'standard'
                    ? '策略结果依据当前有效执行额度与系统结算参数进行记录；进入释放流程的额度将不再纳入后续执行。'
                    : '体验策略不计入合作网络配置规模，也不触发协作激励。'
                }}
              </p>
            </div>
          </div>
          <span class="selected-plan-code">
            PLAN {{ currentPlanId }}
          </span>
        </div>

        <template v-if="orderMode === 'standard'">
          <div class="amount-box">
            <div class="field-label-row">
              <label>策略配置额度</label>
              <span>最低 {{ money(standardMinAmount) }} USDT</span>
            </div>

            <div class="amount-input">
              <input
                v-model="amount"
                type="number"
                inputmode="decimal"
                :min="standardMinAmount"
                placeholder="请输入策略配置额度"
                :disabled="orderSubmitting || loadingConfig"
              />
              <b>USDT</b>
            </div>

            <div class="quick-amounts">
              <button
                v-for="quick in quickAmounts"
                :key="quick"
                type="button"
                :class="{ active: Number(amount) === quick }"
                @click="amount = String(quick)"
                :disabled="orderSubmitting || loadingConfig"
              >
                {{ quick.toLocaleString('en-US') }} U
              </button>
            </div>
          </div>

          <div class="calc-grid standard-grid">
            <div>
              <span>配置总额</span>
              <strong>{{ money(amount) }}</strong>
              <em>USDT</em>
            </div>
            <div>
              <span>AI 策略服务费</span>
              <strong>{{ money(aiFee) }}</strong>
              <em>{{ modelFeePercentText }}%</em>
            </div>
            <div class="active">
              <span>有效执行额度</span>
              <strong>{{ money(poolPrincipal) }}</strong>
              <em>{{ poolRatePercentText }}% EXECUTION ALLOCATION</em>
            </div>
            <div>
              <span>日度结算参数</span>
              <strong>{{ money(userRate) }}%</strong>
              <em>{{ rateSourceText }}</em>
            </div>
          </div>

          <div class="principal-flow">
            <div class="flow-head">
              <span>资产配置路径</span>
              <b>{{ money(amount) }} USDT</b>
            </div>
            <div class="flow-track">
              <div
                class="flow-fee"
                :style="{ width: Number(amount || 0) > 0 ? modelFeePercentText + '%' : '0%' }"
              >
                <span>{{ modelFeePercentText }}%</span>
              </div>
              <div
                class="flow-pool"
                :style="{ width: Number(amount || 0) > 0 ? poolRatePercentText + '%' : '0%' }"
              >
                <span>{{ poolRatePercentText }}%</span>
              </div>
            </div>
            <div class="flow-legend">
              <span><i class="fee"></i>AI 策略服务费 {{ money(aiFee) }} USDT</span>
              <span><i class="pool"></i>有效执行额度 {{ money(poolPrincipal) }} USDT</span>
            </div>
          </div>

          <div class="rule-note standard-note">
            <span class="note-icon">!</span>
            <p>
              当前策略仅以 {{ poolRatePercentText }}% 有效执行额度用于市场执行与结果结算；
              {{ modelFeePercentText }}% AI 策略服务费不纳入可释放额度。
              发起额度释放后，进入线性释放流程的额度将立即停止纳入后续策略执行。
            </p>
          </div>
        </template>

        <template v-else>
          <div class="trial-product-card">
            <div class="trial-product-head">
              <div>
                <span class="trial-label">NEW USER EXPERIENCE</span>
                <h3>{{ trialConfig.days || 7 }}天智能执行体验方案</h3>
                <p>每个钱包地址仅限开通一次，执行周期结束后按规则统一以 USDT 结算。</p>
              </div>
              <div class="trial-amount">
                <span>预设配置额度</span>
                <strong>{{ money(trialConfig.amount) }}</strong>
                <em>USDT</em>
              </div>
            </div>

            <div class="trial-feature-grid">
              <div>
                <span>计划周期</span>
                <strong>{{ trialConfig.days }} 天</strong>
                <em>固定周期</em>
              </div>
              <div>
                <span>预设结算结果</span>
                <strong>{{ trialProfitDisplay }}</strong>
                <em>{{ trialProfitModeText }}</em>
              </div>
              <div>
                <span>到期结算总额</span>
                <strong>{{ money(trialExpectedTotal) }}</strong>
                <em>USDT</em>
              </div>
              <div>
                <span>结算资产</span>
                <strong>USDT</strong>
                <em>USDT 结算</em>
              </div>
            </div>

            <div class="trial-quota-panel">
              <div class="quota-title">
                <div>
                  <span>今日可用额度</span>
                  <b>{{ trialRemainingQuota }} / {{ trialConfig.dailyQuota }}</b>
                </div>
                <em>{{ trialQuotaStatusText }}</em>
              </div>

              <div class="quota-line">
                <div :style="{ width: trialQuotaProgress + '%' }"></div>
              </div>

              <div class="eligibility-grid">
                <div :class="['eligibility-item', { passed: !trialConfig.purchased }]">
                  <i>{{ !trialConfig.purchased ? '✓' : '×' }}</i>
                  <span>
                    <b>钱包开通资格</b>
                    <small>{{ !trialConfig.purchased ? '当前地址尚未开通体验策略' : '当前地址已开通过体验策略' }}</small>
                  </span>
                </div>
                <div :class="['eligibility-item', { passed: trialRemainingQuota > 0 }]">
                  <i>{{ trialRemainingQuota > 0 ? '✓' : '×' }}</i>
                  <span>
                    <b>当日开放状态</b>
                    <small>{{ trialRemainingQuota > 0 ? '当前仍有可用额度' : '今日可用额度已用完' }}</small>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="rule-note trial-note">
            <span class="note-icon">i</span>
            <p>
              体验策略采用 {{ money(trialConfig.amount) }} USDT 预设配置额度与 {{ trialConfig.days }} 天固定执行周期，
              不收取 AI 策略服务费，不计入合作网络配置规模，也不触发协作激励。
              执行周期内不可提前释放额度，周期结束后按规则统一结算。
            </p>
          </div>
        </template>

        <div
          v-if="orderProcess.visible"
          :class="['order-process-panel', orderProcess.status]"
        >
          <div class="process-head">
            <div class="process-head-left">
              <span :class="['process-state-icon', orderProcess.status]">
                <i v-if="orderProcess.status === 'processing'"></i>
                <b v-else-if="orderProcess.status === 'success'">✓</b>
                <b v-else-if="orderProcess.status === 'error'">!</b>
                <b v-else>·</b>
              </span>
              <div>
                <strong>{{ orderProcess.title }}</strong>
                <p>{{ orderProcess.desc }}</p>
              </div>
            </div>
            <span class="process-plan">PLAN {{ currentPlanId }}</span>
          </div>

          <div class="process-track">
            <div :style="{ width: orderProcessPercent + '%' }"></div>
          </div>

          <div class="process-steps">
            <div :class="['process-step', processStepClass(1)]">
              <span>1</span>
              <div>
                <b>校验授权</b>
                <small>读取 USDT 可用授权额度</small>
              </div>
            </div>
            <div :class="['process-step', processStepClass(2)]">
              <span>2</span>
              <div>
                <b>USDT 授权</b>
                <small>{{ orderProcess.approveSkipped ? '额度充足，已跳过' : '等待链上授权确认' }}</small>
              </div>
            </div>
            <div :class="['process-step', processStepClass(3)]">
              <span>3</span>
              <div>
                <b>提交配置</b>
                <small>提交资产配置并等待网络确认</small>
              </div>
            </div>
          </div>

          <div
            v-if="orderProcess.approveHash || orderProcess.depositHash || orderProcess.chainOrderId"
            class="process-chain-data"
          >
            <div v-if="orderProcess.approveHash">
              <span>授权交易</span>
              <strong>{{ shortHash(orderProcess.approveHash) }}</strong>
            </div>
            <div v-if="orderProcess.depositHash">
              <span>配置交易</span>
              <strong>{{ shortHash(orderProcess.depositHash) }}</strong>
            </div>
            <div v-if="orderProcess.chainOrderId">
              <span>链上策略编号</span>
              <strong>#{{ orderProcess.chainOrderId }}</strong>
            </div>
          </div>

          <div v-if="orderProcess.status === 'success'" class="process-success-note">
            链上资产配置已确认，后台识别 DepositCreated 事件后将自动生成策略记录。
          </div>
        </div>

        <button
          class="primary-btn"
          :class="{ trial: orderMode === 'trial', loading: orderSubmitting }"
          :disabled="submitDisabled"
          @click="createOrder"
        >
          <span v-if="orderSubmitting" class="btn-spinner"></span>
          <span>{{ submitButtonText }}</span>
          <small>{{ orderMode === 'standard' ? 'USDT APPROVE & CONFIGURE' : 'ONE ADDRESS · ONE TRIAL' }}</small>
        </button>
      </section>

      <section class="section-card portfolio-card">
        <div class="section-head portfolio-head">
          <div>
            <h2>策略持仓管理</h2>
            <p>不同策略记录采用独立的执行、额度释放与到期结算规则。</p>
          </div>
          <span>Strategy Ledger</span>
        </div>

        <div class="portfolio-summary">
          <div>
            <small>TOTAL RECORDS</small>
            <strong>{{ orderStats.totalCount }}</strong>
            <span>策略记录总数</span>
          </div>
          <div>
            <small>STANDARD STRATEGY</small>
            <strong>{{ orderStats.standardCount }}</strong>
            <span>标准执行策略</span>
          </div>
          <div>
            <small>TRIAL STRATEGY</small>
            <strong>{{ orderStats.trialCount }}</strong>
            <span>体验策略方案</span>
          </div>
        </div>

        <div class="order-filter">
          <button
            type="button"
            :class="{ active: orderFilter === 'all' }"
            @click="changeOrderFilter('all')"
          >
            全部记录
            <span>{{ orderStats.totalCount }}</span>
          </button>
          <button
            type="button"
            :class="{ active: orderFilter === 'standard' }"
            @click="changeOrderFilter('standard')"
          >
            标准策略
            <span>{{ orderStats.standardCount }}</span>
          </button>
          <button
            type="button"
            :class="{ active: orderFilter === 'trial' }"
            @click="changeOrderFilter('trial')"
          >
            体验策略
            <span>{{ orderStats.trialCount }}</span>
          </button>
        </div>

        <div v-if="loadingOrders && !orders.length" class="empty-box">
          <div class="empty-icon">···</div>
          <b>正在加载策略记录</b>
          <p>正在读取你的真实 BCH 策略配置与执行数据。</p>
        </div>

        <div v-else-if="orders.length === 0" class="empty-box">
          <div class="empty-icon">₿</div>
          <b>暂无当前类型记录</b>
          <p>选择上方策略方案，提交你的第一笔策略配置。</p>
        </div>

        <div v-else class="order-list">
          <article
            v-for="item in orders"
            :key="item.orderNo"
            :class="['order-card', item.orderType === 2 ? 'trial-order' : 'standard-order']"
          >
            <div class="order-accent"></div>
            <div class="order-tech-grid"></div>
            <div class="order-scan-line"></div>

            <div class="order-top">
              <div class="order-identity">
                <div class="order-type-row">
                  <span :class="['order-type-badge', item.orderType === 2 ? 'trial' : 'standard']">
                    {{ orderTypeText(item.orderType) }}
                  </span>
                  <span v-if="item.orderType === 2" class="order-rule-chip">
                    {{ item.trialDays || trialConfig.days }} DAY
                  </span>
                  <span v-else class="order-rule-chip">
                    {{ poolRatePercentText }}% EXEC
                  </span>
                </div>
                <h3>{{ item.orderNo }}</h3>
                <p>{{ formatDateTime(item.createTime) }}</p>
              </div>

              <span :class="['status', statusClass(item)]">
                <i></i>
                {{ statusText(item) }}
              </span>
            </div>

            <div v-if="item.orderType === 1" class="order-data">
              <div>
                <span>配置总额</span>
                <strong>{{ money(item.depositAmountUsdt) }}</strong>
                <em>USDT</em>
              </div>
              <div>
                <span>AI 策略服务费</span>
                <strong>{{ money(item.modelFeeUsdt) }}</strong>
                <em>{{ percentFromRate(item.modelFeeRate, modelFeePercentText) }}%</em>
              </div>
              <div class="highlight-data">
                <span>执行中额度</span>
                <strong>{{ money(item.earningPrincipalUsdt) }}</strong>
                <em>USDT</em>
              </div>
              <div>
                <span>累计结算结果</span>
                <strong>+{{ money(item.profitStaticUsdt) }}</strong>
                <em>USDT</em>
              </div>
            </div>

            <div v-else class="order-data trial-data">
              <div>
                <span>配置额度</span>
                <strong>{{ money(item.depositAmountUsdt) }}</strong>
                <em>USDT</em>
              </div>
              <div>
                <span>预设结算结果</span>
                <strong>+{{ money(item.trialProfitUsdt) }}</strong>
                <em>USDT</em>
              </div>
              <div class="highlight-data">
                <span>到期结算总额</span>
                <strong>{{ money(item.expectedTotalUsdt) }}</strong>
                <em>USDT</em>
              </div>
              <div>
                <span>计划结算日期</span>
                <strong class="date-value">{{ item.maturityDate || '--' }}</strong>
                <em>{{ item.canWithdraw ? '已到结算期' : '策略执行中' }}</em>
              </div>
            </div>

            <div v-if="item.orderType === 1" class="progress-box">
              <div class="progress-row">
                <span>额度释放进度</span>
                <b>{{ money(item.redeemProgress) }}%</b>
              </div>
              <div class="progress-line">
                <div :style="{ width: progressWidth(item.redeemProgress) }"></div>
              </div>
              <div class="progress-meta">
                <span>执行中额度 {{ money(item.earningPrincipalUsdt) }} U</span>
                <span>释放处理中 {{ money(item.releasingPrincipalUsdt) }} U</span>
                <span>已释放 {{ money(item.redeemedPrincipalUsdt) }} U</span>
              </div>
            </div>

            <div v-else class="progress-box trial-progress-box">
              <div class="progress-row">
                <span>执行周期</span>
                <b>{{ item.settledDays }} / {{ item.trialDays }} 天</b>
              </div>
              <div class="progress-line trial-progress">
                <div :style="{ width: progressWidth(item.cycleProgress) }"></div>
              </div>
              <div class="progress-meta">
                <span>开始 {{ item.startDate || '--' }}</span>
                <span>
                  {{ item.canWithdraw ? '已到结算期，可提交结算' : '距离结算剩余 ' + item.remainingDays + ' 天' }}
                </span>
              </div>
            </div>

            <div class="order-actions">
              <button type="button" @click="goOrderDetail(item)">
                <span>策略详情</span>
              </button>

              <button
                v-if="item.orderType === 1"
                type="button"
                class="gold"
                :disabled="!canRedeem(item)"
                @click="goRedeem(item)"
              >
                <span>{{ canRedeem(item) ? '申请额度释放' : '暂不可释放' }}</span>
              </button>

              <button
                v-else
                type="button"
                :class="['trial-action', { ready: item.canWithdraw }]"
                :disabled="!item.canWithdraw"
                @click="claimTrial(item)"
              >
                <span>{{ item.canWithdraw ? '提交到期结算' : '到期后可结算' }}</span>
              </button>
            </div>
          </article>
        </div>

        <div v-if="orders.length" class="load-more-area">
          <button
            v-if="hasMore"
            type="button"
            class="load-more-btn-dark"
            :disabled="loadingMore"
            @click="loadMoreOrders"
          >
            {{ loadingMore ? '正在加载...' : '加载更多记录' }}
          </button>
          <p v-else>已显示全部 {{ total }} 条策略记录</p>
        </div>
      </section>

      <BchOrderDetailDrawer
  :open="detailOpen"
  :order="detailOrder"
  :wallet-address="walletAddress"
  @close="closeOrderDetail"
  @redeem="handleDetailRedeem"
  @claim-trial="handleDetailTrialClaim"
/>

      <BchOrderRedeemDrawer
  :open="redeemOpen"
  :order="redeemOrder"
  :btc-price-usdt="btcPriceUsdt"
  :redeem-options="redeemOptions"
  @close="closeRedeemDrawer"
  @success="handleBchRedeemSuccess"
/>

      <AppFooter />
    </main>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import BchOrderDetailDrawer from '@/components/BchOrderDetailDrawer.vue'
import BchOrderRedeemDrawer from '@/components/BchOrderRedeemDrawer.vue'
import wallet from '@/store/wallet'
import boxaiVaultService from '@/services/boxaiVaultService'
import {
  fetchBchArbitrageConfig,
  fetchBchOrderStat,
  fetchBchOrderPage,
  fetchBchOrderDetail,
  applyBchPrincipalRedeem,
  claimBchTrialOrder
} from '@/api/bchArbitrage'

function readStorageToken() {
  const keys = ['token', 'access_token', 'Access-Token', 'Authorization', 'AUTH_TOKEN']

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const localVal = window.localStorage.getItem(key)
    if (localVal) return localVal

    const sessionVal = window.sessionStorage.getItem(key)
    if (sessionVal) return sessionVal
  }

  return ''
}

function extractResponseData(res) {
  if (!res) return {}

  if (res.data && res.data.code !== undefined && Object.prototype.hasOwnProperty.call(res.data, 'data')) {
    return res.data.data || {}
  }

  if (res.code !== undefined && Object.prototype.hasOwnProperty.call(res, 'data')) {
    return res.data || {}
  }

  if (Object.prototype.hasOwnProperty.call(res, 'data')) {
    return res.data || {}
  }

  return res
}

function safeNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

export default {
  name: 'InvestPool',
  components: {
    AppHeader,
    AppFooter,
    BchOrderDetailDrawer,
    BchOrderRedeemDrawer
  },
  data() {
    return {
      wallet,
      walletPoller: null,
      lastAuthKey: '',
      refreshTimers: [],

      loadingConfig: false,
      loadingOrders: false,
      loadingMore: false,
      loadingStat: false,
      configReady: false,
      errorMsg: '',

      detailOpen: false,
      detailOrder: null,
      detailLoading: false,

      redeemOpen: false,
      redeemOrder: null,
      redeemSubmitting: false,

      orderSubmitting: false,
      orderProcess: {
        visible: false,
        status: 'idle',
        stepIndex: 0,
        title: '准备提交策略配置',
        desc: '',
        approveSkipped: false,
        approveHash: '',
        depositHash: '',
        chainOrderId: '',
        clientOrderKey: ''
      },

      btcPriceUsdt: 65000,

      standardPlanId: 100,
      trialPlanId: 1,
      standardMinAmount: 1,
      modelFeeRate: 0.3,

      redeemOptions: [
        { days: 7, label: '快速线性释放', feeRate: 0.10 },
        { days: 30, label: '标准线性释放', feeRate: 0 }
      ],

      orderMode: 'standard',
      orderFilter: 'all',
      amount: '',
      quickAmounts: [1000, 3000, 5000, 10000],

      userRate: 0,
      rateSource: 'system',

      trialConfig: {
        amount: 0,
        days: 7,
        profitMode: 'amount',
        profitValue: 0,
        dailyQuota: 0,
        usedQuota: 0,
        remainingQuota: 0,
        purchased: false,
        available: false
      },

      orderStats: {
        totalCount: 0,
        standardCount: 0,
        trialCount: 0
      },

      pageNum: 1,
      pageSize: 10,
      total: 0,
      hasMore: false,
      orders: []
    }
  },
  computed: {
    walletAddress() {
      const state = this.wallet && this.wallet.state ? this.wallet.state : {}
      return (
        (this.wallet && (this.wallet.address || this.wallet.account || this.wallet.userCode)) ||
        state.address ||
        state.account ||
        state.walletAddress ||
        state.userAddress ||
        state.userCode ||
        ''
      )
    },
    isLoggedIn() {
      return !!this.getAuthKey()
    },
    modelFeePercentText() {
      return this.trimNumber(this.modelFeeRate * 100)
    },
    poolRate() {
      return Math.max(1 - safeNumber(this.modelFeeRate, 0), 0)
    },
    poolRatePercentText() {
      return this.trimNumber(this.poolRate * 100)
    },
    currentPlanId() {
      return this.orderMode === 'trial' ? this.trialPlanId : this.standardPlanId
    },
    currentOrderAmount() {
      return this.orderMode === 'trial'
        ? String(this.trialConfig.amount || '')
        : String(this.amount || '').trim()
    },
    orderProcessPercent() {
      if (this.orderProcess.status === 'success') return 100
      if (this.orderProcess.stepIndex <= 0) return 0
      if (this.orderProcess.stepIndex === 1) return 20
      if (this.orderProcess.stepIndex === 2) return 55
      if (this.orderProcess.stepIndex === 3) return 82
      return 100
    },
    aiFee() {
      return safeNumber(this.amount) * safeNumber(this.modelFeeRate)
    },
    poolPrincipal() {
      return safeNumber(this.amount) * this.poolRate
    },
    rateSourceText() {
      const map = {
        system: '系统统一参数',
        account: '账户专属参数',
        order: '策略专属参数',
        trial: '体验策略预设结算结果'
      }
      return map[this.rateSource] || '系统统一参数'
    },
    trialProfitUsdt() {
      if (this.trialConfig.profitMode === 'rate') {
        return safeNumber(this.trialConfig.amount) * safeNumber(this.trialConfig.profitValue) / 100
      }
      return safeNumber(this.trialConfig.profitValue)
    },
    trialExpectedTotal() {
      return safeNumber(this.trialConfig.amount) + this.trialProfitUsdt
    },
    trialProfitDisplay() {
      if (this.trialConfig.profitMode === 'rate') {
        return this.trimNumber(this.trialConfig.profitValue) + '%'
      }
      return this.money(this.trialConfig.profitValue) + ' USDT'
    },
    trialProfitModeText() {
      return this.trialConfig.profitMode === 'rate' ? '预设结算结果比例' : 'USDT 预设结算结果'
    },
    trialRemainingQuota() {
      if (this.trialConfig.remainingQuota !== null && this.trialConfig.remainingQuota !== undefined) {
        return Math.max(safeNumber(this.trialConfig.remainingQuota), 0)
      }

      return Math.max(
        safeNumber(this.trialConfig.dailyQuota) - safeNumber(this.trialConfig.usedQuota),
        0
      )
    },
    trialQuotaProgress() {
      const total = safeNumber(this.trialConfig.dailyQuota)
      if (total <= 0) return 100
      return Math.min(safeNumber(this.trialConfig.usedQuota) / total * 100, 100)
    },
    trialQuotaStatusText() {
      if (this.loadingConfig) return '读取中'
      if (this.trialRemainingQuota <= 0) return '今日额度已用完'
      if (this.trialRemainingQuota <= 10) return '可用额度较少'
      return '当前可配置'
    },
    canCreateTrial() {
      return this.configReady &&
        !this.trialConfig.purchased &&
        this.trialRemainingQuota > 0 &&
        safeNumber(this.trialConfig.amount) > 0
    },
    submitDisabled() {
      if (!this.isLoggedIn || this.orderSubmitting || this.loadingConfig || !this.configReady) return true
      if (this.orderMode === 'trial') return !this.canCreateTrial
      return !this.isValidAmount(this.currentOrderAmount)
    },
    submitButtonText() {
      if (!this.isLoggedIn) return '请先连接钱包并登录'
      if (this.loadingConfig) return '正在读取策略参数'

      if (this.orderSubmitting) {
        const stepText = {
          1: '正在校验 USDT 授权额度',
          2: '请在钱包中完成 USDT 授权',
          3: '正在提交策略配置并等待确认',
          4: '链上策略配置已确认'
        }
        return stepText[this.orderProcess.stepIndex] || '正在处理配置交易'
      }

      if (this.orderMode === 'standard') {
        return '授权 USDT 并提交标准策略'
      }
      if (this.trialConfig.purchased) return '当前地址已开通体验策略'
      if (this.trialRemainingQuota <= 0) return '今日体验额度已用完'
      return `授权 ${this.money(this.trialConfig.amount)} USDT 并开通体验策略`
    }
  },
  mounted() {
    this.lastAuthKey = this.getAuthKey()
    this.bindEvents()

    if (this.isLoggedIn) {
      this.loadPageData()
    }

    this.walletPoller = window.setInterval(() => {
      const authKey = this.getAuthKey()
      if (authKey === this.lastAuthKey) return

      this.lastAuthKey = authKey
      this.clearRefreshTimers()

      if (authKey) {
        this.loadPageData()
      } else {
        this.resetRemoteData()
      }
    }, 600)
  },
  activated() {
    if (this.isLoggedIn) {
      this.loadPageData()
    }
  },
  beforeDestroy() {
    if (this.walletPoller) window.clearInterval(this.walletPoller)
    this.unbindEvents()
    this.clearRefreshTimers()
  },
  methods: {
    async handleBchRedeemSuccess(result) {
  this.redeemOpen = false
  this.redeemOrder = null

  try {
    await Promise.all([
      this.loadBchConfig(),
      this.loadOrderStat(),
      this.loadOrders(true)
    ])

    const actionText =
      result && result.action === 'TRIAL_MATURITY_CLAIM'
        ? '体验策略到期结算完成'
        : '额度释放申请完成'

    console.log(actionText, result)
  } catch (error) {
    console.error('额度释放成功后刷新策略记录失败：', error)
  }
},
    bindEvents() {
      window.addEventListener('auth:login', this.handleAuthLogin)
      window.addEventListener('auth:logout', this.handleAuthLogout)
    },
    unbindEvents() {
      window.removeEventListener('auth:login', this.handleAuthLogin)
      window.removeEventListener('auth:logout', this.handleAuthLogout)
    },
    handleAuthLogin() {
      this.lastAuthKey = this.getAuthKey()
      this.loadPageData()
    },
    handleAuthLogout() {
      this.lastAuthKey = ''
      this.resetRemoteData()
    },
    getAuthKey() {
      const state = this.wallet && this.wallet.state ? this.wallet.state : {}
      const token =
        state.token ||
        state.accessToken ||
        (this.wallet && (this.wallet.token || this.wallet.accessToken)) ||
        readStorageToken()

      return [token, this.walletAddress].filter(Boolean).join('|')
    },
    resetRemoteData() {
      this.configReady = false
      this.errorMsg = ''
      this.orders = []
      this.pageNum = 1
      this.total = 0
      this.hasMore = false
      this.orderStats = {
        totalCount: 0,
        standardCount: 0,
        trialCount: 0
      }
    },
    async loadPageData() {
      if (!this.isLoggedIn) {
        this.resetRemoteData()
        return
      }

      this.errorMsg = ''

      try {
        await Promise.all([
          this.loadBchConfig(),
          this.loadOrderStat(),
          this.loadOrders(true)
        ])
      } catch (error) {
        this.errorMsg = this.extractErrorMessage(error, '加载 BCH 策略数据失败')
      }
    },
    async loadBchConfig() {
      if (!this.isLoggedIn) return

      this.loadingConfig = true

      try {
        const res = await fetchBchArbitrageConfig({ silent: true })
        const data = extractResponseData(res)
        const trial = data.trialConfig || {}

        this.standardPlanId = safeNumber(data.standardPlanId, 100)
        this.trialPlanId = safeNumber(data.trialPlanId, 1)
        this.standardMinAmount = safeNumber(data.standardMinAmount, 1)
        this.modelFeeRate = safeNumber(data.modelFeeRate, 0.3)
        this.userRate = safeNumber(data.userRate, 0)
        this.rateSource = data.rateSource || 'system'

        this.trialConfig = {
          amount: safeNumber(trial.amount, 0),
          days: safeNumber(trial.days, 7),
          profitMode: trial.profitMode || 'amount',
          profitValue: safeNumber(trial.profitValue, 0),
          dailyQuota: safeNumber(trial.dailyQuota, 0),
          usedQuota: safeNumber(trial.usedQuota, 0),
          remainingQuota: safeNumber(trial.remainingQuota, 0),
          purchased: Boolean(trial.purchased),
          available: Boolean(trial.available)
        }

        if (Array.isArray(data.redeemOptions) && data.redeemOptions.length) {
          this.redeemOptions = data.redeemOptions.map(item => ({
            days: safeNumber(item.days),
            label: item.label || '',
            feeRate: safeNumber(item.feeRate)
          }))
        }

        this.configReady = true
      } finally {
        this.loadingConfig = false
      }
    },
    async loadOrderStat() {
      if (!this.isLoggedIn) return

      this.loadingStat = true

      try {
        const res = await fetchBchOrderStat({ silent: true })
        const data = extractResponseData(res)

        this.orderStats = {
          totalCount: safeNumber(data.totalCount),
          standardCount: safeNumber(data.standardCount),
          trialCount: safeNumber(data.trialCount)
        }
      } finally {
        this.loadingStat = false
      }
    },
    normalizeOrderItem(item) {
      const order = item || {}
      const orderType = safeNumber(order.orderType)
      const depositAmount = safeNumber(order.depositAmountUsdt || order.amount)
      const trialProfit = safeNumber(order.trialProfitUsdt)

      return {
        ...order,
        orderNo: order.orderNo || '',
        orderType,
        status: safeNumber(order.status),
        redeemStatus: safeNumber(order.redeemStatus),
        depositAmountUsdt: depositAmount,
        modelFeeRate: safeNumber(order.modelFeeRate),
        modelFeeUsdt: safeNumber(order.modelFeeUsdt),
        arbitragePrincipalUsdt: safeNumber(order.arbitragePrincipalUsdt),
        earningPrincipalUsdt: safeNumber(order.earningPrincipalUsdt),
        releasingPrincipalUsdt: safeNumber(order.releasingPrincipalUsdt),
        redeemedPrincipalUsdt: safeNumber(order.redeemedPrincipalUsdt),
        profitStaticUsdt: safeNumber(order.profitStaticUsdt || order.income),
        availableProfitUsdt: safeNumber(order.availableProfitUsdt),
        withdrawingProfitUsdt: safeNumber(order.withdrawingProfitUsdt),
        withdrawnProfitUsdt: safeNumber(order.withdrawnProfitUsdt),
        trialProfitUsdt: trialProfit,
        expectedTotalUsdt: safeNumber(
          order.expectedTotalUsdt,
          orderType === 2 ? depositAmount + trialProfit : 0
        ),
        redeemProgress: safeNumber(order.redeemProgress),
        cycleProgress: safeNumber(order.cycleProgress),
        settledDays: safeNumber(order.settledDays),
        trialDays: safeNumber(order.trialDays, this.trialConfig.days || 7),
        remainingDays: safeNumber(order.remainingDays),
        canRedeem: typeof order.canRedeem === 'boolean' ? order.canRedeem : undefined,
        canWithdraw: Boolean(order.canWithdraw),
        trialClaimApplied: Boolean(order.trialClaimApplied),
        rate: order.rate || '',
        statusText: order.statusText || '',
        statusClass: order.statusClass || ''
      }
    },
    async loadOrders(reset = true) {
      if (!this.isLoggedIn) return
      if (reset && this.loadingOrders) return
      if (!reset && (this.loadingMore || !this.hasMore)) return

      if (reset) {
        this.loadingOrders = true
        this.pageNum = 1
      } else {
        this.loadingMore = true
      }

      try {
        const targetPage = reset ? 1 : this.pageNum + 1
        const params = {
          pageNum: targetPage,
          pageSize: this.pageSize
        }

        if (this.orderFilter === 'standard') params.orderType = 1
        if (this.orderFilter === 'trial') params.orderType = 2

        const res = await fetchBchOrderPage(params, { silent: true })
        const page = extractResponseData(res)
        const records = Array.isArray(page.records)
          ? page.records.map(this.normalizeOrderItem)
          : []

        if (reset) {
          this.orders = records
        } else {
          const orderMap = {}
          this.orders.forEach(item => { orderMap[item.orderNo] = item })
          records.forEach(item => { orderMap[item.orderNo] = item })
          this.orders = Object.values(orderMap)
        }

        this.pageNum = safeNumber(page.current || page.pageNum, targetPage)
        this.pageSize = safeNumber(page.size || page.pageSize, this.pageSize)
        this.total = safeNumber(page.total)
        this.hasMore = this.orders.length < this.total
      } finally {
        this.loadingOrders = false
        this.loadingMore = false
      }
    },
    loadMoreOrders() {
      return this.loadOrders(false)
    },
    async changeOrderFilter(filter) {
      if (this.orderFilter === filter || this.loadingOrders) return
      this.orderFilter = filter

      try {
        await this.loadOrders(true)
      } catch (error) {
        this.toast(this.extractErrorMessage(error, '加载策略记录失败'), 'error')
      }
    },
    selectOrderMode(mode) {
      this.orderMode = mode
      if (mode === 'trial') {
        this.amount = String(this.trialConfig.amount || '')
      } else if (Number(this.amount) === Number(this.trialConfig.amount)) {
        this.amount = ''
      }
    },
    money(val) {
      return safeNumber(val).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4
      })
    },
    trimNumber(val) {
      const number = safeNumber(val)
      return String(Number(number.toFixed(6)))
    },
    percentFromRate(rate, fallback) {
      if (rate === null || rate === undefined || rate === '') return fallback
      return this.trimNumber(safeNumber(rate) * 100)
    },
    formatDateTime(date) {
      if (!date) return '--'
      return String(date).replace('T', ' ').slice(0, 16)
    },
    isValidAmount(value) {
      const text = String(value == null ? '' : value).trim()
      if (!/^\d+(\.\d{1,18})?$/.test(text)) return false
      const orderAmount = Number(text)
      return Number.isFinite(orderAmount) && orderAmount >= safeNumber(this.standardMinAmount, 1)
    },
    resetOrderProcess() {
      this.orderProcess = {
        visible: true,
        status: 'processing',
        stepIndex: 1,
        title: '正在校验授权额度',
        desc: '系统正在读取当前钱包对策略配置合约的 USDT 授权额度。',
        approveSkipped: false,
        approveHash: '',
        depositHash: '',
        chainOrderId: '',
        clientOrderKey: ''
      }
    },
    processStepClass(index) {
      if (this.orderProcess.status === 'error' && this.orderProcess.stepIndex === index) return 'error'
      if (this.orderProcess.status === 'success' || this.orderProcess.stepIndex > index) return 'done'
      if (this.orderProcess.stepIndex === index) return 'active'
      return 'pending'
    },
    shortHash(hash) {
      const text = String(hash || '')
      if (text.length <= 18) return text
      return text.slice(0, 10) + '...' + text.slice(-8)
    },
    handleOrderStep(step, payload) {
      const data = payload || {}

      if (step === 'CHECK_ALLOWANCE') {
        this.orderProcess.stepIndex = 1
        this.orderProcess.title = '正在校验 USDT 授权额度'
        this.orderProcess.desc = '正在确认当前钱包是否已向 BCH 策略配置合约授权足够额度。'
        this.orderProcess.clientOrderKey = data.clientOrderKey || ''
        return
      }

      if (step === 'APPROVE_REQUIRED') {
        this.orderProcess.stepIndex = 2
        this.orderProcess.title = '需要完成 USDT 授权'
        this.orderProcess.desc = '请在钱包中确认授权交易，授权确认后系统会自动继续提交策略配置。'
        return
      }

      if (step === 'APPROVE_TX') {
        this.orderProcess.stepIndex = 2
        this.orderProcess.approveHash = data.hash || ''
        this.orderProcess.title = 'USDT 授权交易已提交'
        this.orderProcess.desc = '正在等待 BSC 网络确认授权交易，请勿关闭页面或重复提交。'
        return
      }

      if (step === 'APPROVE_CONFIRMED') {
        this.orderProcess.stepIndex = 3
        this.orderProcess.title = 'USDT 授权已经确认'
        this.orderProcess.desc = '授权完成，系统正在准备提交策略资产配置交易。'
        return
      }

      if (step === 'APPROVE_SKIPPED') {
        this.orderProcess.approveSkipped = true
        this.orderProcess.stepIndex = 3
        this.orderProcess.title = 'USDT 授权额度充足'
        this.orderProcess.desc = '无需重复授权，系统将直接提交策略资产配置交易。'
        return
      }

      if (step === 'DEPOSIT_SENDING') {
        this.orderProcess.stepIndex = 3
        this.orderProcess.title = '正在提交链上策略配置'
        this.orderProcess.desc = '请在钱包中确认资产配置交易，确认后 USDT 将进入 BCH 策略配置合约。'
        return
      }

      if (step === 'DEPOSIT_TX') {
        this.orderProcess.stepIndex = 3
        this.orderProcess.depositHash = data.hash || ''
        this.orderProcess.title = '策略配置交易已提交'
        this.orderProcess.desc = '正在等待 BSC 网络确认，后台随后会识别事件并生成策略记录。'
        return
      }

      if (step === 'DEPOSIT_CONFIRMED') {
        this.orderProcess.stepIndex = 4
        this.orderProcess.status = 'success'
        this.orderProcess.depositHash = data.hash || this.orderProcess.depositHash
        this.orderProcess.chainOrderId = data.parsed && data.parsed.orderId
          ? String(data.parsed.orderId)
          : ''
        this.orderProcess.title = '链上策略配置已确认'
        this.orderProcess.desc = this.orderProcess.chainOrderId
          ? '链上策略 #' + this.orderProcess.chainOrderId + ' 已确认，等待后台同步策略记录。'
          : '资产配置交易已经确认，等待后台识别链上事件并同步策略记录。'
      }
    },
    isReceiptFailed(receipt) {
      if (!receipt) return false
      const status = receipt.status
      return status === false || status === 0 || status === '0x0' || status === '0'
    },
    normalizeOrderError(error) {
      const code = error && error.code
      const raw = String(
        (error && (error.shortMessage || error.reason || error.message)) ||
        '链上策略配置失败'
      )

      if (code === 4001 || /user rejected|user denied|拒绝|取消/i.test(raw)) {
        return '你已取消钱包确认，本次策略配置未提交。'
      }
      if (code === -32002 || /already pending|request already pending/i.test(raw)) {
        return '钱包中已有待处理请求，请先打开钱包完成或取消上一笔操作。'
      }
      if (/insufficient funds|gas required exceeds allowance/i.test(raw)) {
        return '钱包 BNB 不足，无法支付 BSC 网络矿工费。'
      }
      if (/transfer amount exceeds balance|ERC20_OP_FAIL|ERC20_CALL_FAIL|NO_USDT/i.test(raw)) {
        return '钱包 USDT 余额不足，或 USDT 转账执行失败。'
      }
      if (/DUP_KEY/i.test(raw)) {
        return '本次策略配置请求已提交，请刷新策略记录确认结果。'
      }
      if (/PAUSED/i.test(raw)) {
        return '当前资产配置功能已暂停，请稍后再试。'
      }
      if (/VENUS_MINT_FAIL/i.test(raw)) {
        return '资产配置至 Venus 流动性协议失败，链上交易已回滚。'
      }
      if (/VTOKEN_SEND_FAIL/i.test(raw)) {
        return 'vToken 转发失败，链上交易已回滚，请联系平台技术人员检查接收地址。'
      }
      if (/execution reverted/i.test(raw)) {
        return '合约执行失败：' + raw.replace(/^.*execution reverted:?\s*/i, '')
      }
      return raw
    },
    async createOrder() {
      if (this.orderSubmitting) return

      const isTrial = this.orderMode === 'trial'
      const planId = isTrial ? this.trialPlanId : this.standardPlanId
      const orderAmount = isTrial
        ? String(this.trialConfig.amount || '')
        : String(this.amount || '').trim()

      if (isTrial) {
        if (this.trialConfig.purchased) {
          return this.toast({
            title: '无法开通体验策略',
            desc: '每个钱包地址仅限开通一次体验策略。',
            duration: 3000
          }, 'error')
        }
        if (this.trialRemainingQuota <= 0) {
          return this.toast({
            title: '今日体验额度已用完',
            desc: '今日体验策略额度已经用完，请在下一个开放周期再提交。',
            duration: 3000
          }, 'error')
        }
      }

      if (!this.isValidAmount(orderAmount) && !isTrial) {
        return this.toast({
          title: '策略配置额度不正确',
          desc: `请输入不低于 ${this.money(this.standardMinAmount)} USDT 的策略配置额度。`,
          duration: 3000
        }, 'error')
      }

      if (isTrial && safeNumber(orderAmount) <= 0) {
        return this.toast({
          title: '配置额度配置错误',
          desc: '后台尚未返回有效的体验策略配置额度。',
          duration: 3000
        }, 'error')
      }

      this.orderSubmitting = true
      this.resetOrderProcess()

      try {
        // 保留原有链上资产配置流程，不修改授权与支付方法。
        const result = await boxaiVaultService.placeOrder({
          planId,
          amount: orderAmount,
          onStep: (step, payload) => this.handleOrderStep(step, payload)
        })

        if (this.isReceiptFailed(result && result.depositRcpt)) {
          throw new Error('策略资产配置交易执行失败，链上状态为失败')
        }

        const chainOrderId = result && result.parsed && result.parsed.orderId
          ? String(result.parsed.orderId)
          : ''

        this.orderProcess.status = 'success'
        this.orderProcess.stepIndex = 4
        this.orderProcess.depositHash = (result && result.depositHash) || this.orderProcess.depositHash
        this.orderProcess.chainOrderId = chainOrderId
        this.orderProcess.clientOrderKey = (result && result.clientOrderKey) || this.orderProcess.clientOrderKey
        this.orderProcess.title = '策略配置成功'
        this.orderProcess.desc = chainOrderId
          ? '链上策略 #' + chainOrderId + ' 已确认，后台正在同步策略记录。'
          : '链上资产配置已经确认，后台正在识别事件并同步策略记录。'

        if (isTrial) {
          this.trialConfig.purchased = true
          this.trialConfig.available = false
          this.trialConfig.usedQuota = Math.min(
            safeNumber(this.trialConfig.usedQuota) + 1,
            safeNumber(this.trialConfig.dailyQuota)
          )
          this.trialConfig.remainingQuota = Math.max(
            safeNumber(this.trialConfig.dailyQuota) - safeNumber(this.trialConfig.usedQuota),
            0
          )
        } else {
          this.amount = ''
        }

        this.toast({
          title: isTrial ? '体验策略配置成功' : '标准策略配置成功',
          desc: chainOrderId
            ? '链上策略 #' + chainOrderId + ' 已确认，后台同步后会显示在策略持仓中。'
            : '链上资产配置已经确认，后台同步后会显示在策略持仓中。',
          duration: 3200
        }, 'success')

        this.$emit('order-created', {
          planId,
          orderType: isTrial ? 2 : 1,
          amount: orderAmount,
          result
        })

        this.scheduleOrderSync()
      } catch (error) {
        const message = this.normalizeOrderError(error)
        console.error('提交 BCH 策略配置失败：', error)

        this.orderProcess.visible = true
        this.orderProcess.status = 'error'
        this.orderProcess.title = '策略配置失败'
        this.orderProcess.desc = message

        this.toast({
          title: '策略配置失败',
          desc: message,
          duration: 3600
        }, 'error')
      } finally {
        this.orderSubmitting = false
      }
    },
    scheduleOrderSync() {
      this.clearRefreshTimers()

      const refresh = async () => {
        if (!this.isLoggedIn) return
        try {
          await Promise.all([
            this.loadBchConfig(),
            this.loadOrderStat(),
            this.loadOrders(true)
          ])
        } catch (error) {
          console.warn('等待后台同步 BCH 策略记录：', error)
        }
      }

      this.refreshTimers.push(window.setTimeout(refresh, 2500))
      this.refreshTimers.push(window.setTimeout(refresh, 6500))
    },
    clearRefreshTimers() {
      this.refreshTimers.forEach(timer => window.clearTimeout(timer))
      this.refreshTimers = []
    },
    orderTypeText(orderType) {
      return Number(orderType) === 2 ? '体验策略' : '标准执行策略'
    },
    statusText(item) {
      if (item && item.statusText) return item.statusText

      if (Number(item.orderType) === 1 && Number(item.redeemStatus) === 1) return '部分额度释放中'
      if (Number(item.orderType) === 1 && Number(item.redeemStatus) === 2) return '全部额度释放中'

      const map = {
        0: '待网络确认',
        1: '策略执行中',
        2: Number(item.orderType) === 2 ? '待到期结算' : '执行已停止',
        3: '已完成结算',
        4: '策略已取消',
        5: '执行处理失败'
      }
      return map[Number(item.status)] || '状态待同步'
    },
    statusClass(item) {
      if (item && item.statusClass) return item.statusClass

      if (Number(item.orderType) === 1 && [1, 2].indexOf(Number(item.redeemStatus)) !== -1) {
        return 'redeeming'
      }

      const map = {
        0: 'pending',
        1: 'running',
        2: Number(item.orderType) === 2 ? 'matured' : 'stopped',
        3: 'finished',
        4: 'cancelled',
        5: 'failed'
      }
      return map[Number(item.status)] || 'pending'
    },
    progressWidth(value) {
      const progress = Math.max(0, Math.min(safeNumber(value), 100))
      return progress + '%'
    },
    canRedeem(item) {
      if (typeof item.canRedeem === 'boolean') return item.canRedeem

      return Number(item.orderType) === 1 &&
        Number(item.status) === 1 &&
        safeNumber(item.earningPrincipalUsdt) > 0 &&
        safeNumber(item.releasingPrincipalUsdt) <= 0 &&
        Number(item.redeemStatus || 0) !== 2
    },
    async goOrderDetail(item) {
      if (!item || !item.orderNo) {
        return this.toast('策略记录不存在', 'error')
      }

      this.detailOrder = { ...item }
      this.detailOpen = true
      this.detailLoading = true

      try {
        const res = await fetchBchOrderDetail(item.orderNo, { silent: true })
        const data = extractResponseData(res)
        this.detailOrder = this.normalizeOrderItem({ ...item, ...data })
      } catch (error) {
        this.toast(this.extractErrorMessage(error, '加载策略详情失败'), 'error')
      } finally {
        this.detailLoading = false
      }
    },
    closeOrderDetail() {
      this.detailOpen = false
      window.setTimeout(() => {
        this.detailOrder = null
      }, 280)
    },
    handleDetailRedeem(item) {
      this.detailOpen = false
      this.detailOrder = null
      this.openRedeemDrawer(item)
    },
    handleDetailTrialClaim(item) {
      this.detailOpen = false
      this.detailOrder = null
      this.openRedeemDrawer(item)
    },
    openRedeemDrawer(item) {
      if (!item) return this.toast('策略记录不存在', 'error')
      this.redeemOrder = { ...item }
      this.redeemOpen = true
    },
    closeRedeemDrawer() {
      if (this.redeemSubmitting) return

      this.redeemOpen = false
      window.setTimeout(() => {
        if (!this.redeemOpen) this.redeemOrder = null
      }, 280)
    },
    goRedeem(item) {
      if (!this.canRedeem(item)) {
        return this.toast('当前策略暂不可申请额度释放', 'error')
      }
      this.openRedeemDrawer(item)
    },
    claimTrial(item) {
      if (!item) return this.toast('体验策略记录不存在', 'error')
      if (!item.canWithdraw) return this.toast('体验策略尚未到结算期或已提交结算', 'error')
      this.openRedeemDrawer(item)
    },
    extractErrorMessage(error, fallback) {
      if (!error) return fallback
      if (error.response && error.response.data) {
        return error.response.data.msg || error.response.data.message || fallback
      }
      return error.msg || error.message || fallback
    },
    toast(msg, type = 'info') {
      if (this.$toast) {
        if (typeof msg === 'string') {
          if (typeof this.$toast[type] === 'function') return this.$toast[type](msg)
          if (typeof this.$toast.show === 'function') return this.$toast.show(msg)
          if (typeof this.$toast === 'function') return this.$toast(msg)
        } else {
          if (typeof this.$toast.show === 'function') return this.$toast.show(msg)
          if (typeof this.$toast === 'function') return this.$toast(msg)
        }
      }

      if (this.$message) {
        const fallbackText = typeof msg === 'string'
          ? msg
          : (msg && (msg.desc || msg.title)) || '系统提示'

        if (typeof this.$message[type] === 'function') return this.$message[type](fallbackText)
        if (typeof this.$message === 'function') return this.$message(fallbackText)
      }

      const fallbackText = typeof msg === 'string'
        ? msg
        : (msg && (msg.desc || msg.title)) || '系统提示'
      window.alert(fallbackText)
    }
  }
}
</script>

<style lang="less" scoped>
.invest-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 50% -8%, rgba(212, 175, 55, .18), transparent 28%),
    radial-gradient(circle at 100% 18%, rgba(35, 113, 255, .18), transparent 32%),
    linear-gradient(180deg, #05070d 0%, #070d18 46%, #0b1220 100%);
  color: #fff;
}

button,
input {
  font-family: inherit;
}

button {
  -webkit-tap-highlight-color: transparent;
}

.page-body {
  padding: 0 12px 28px;
}

.hero-premium {
  position: relative;
  overflow: hidden;
  min-height: 560px;
  padding: 30px 18px 24px;
  border-radius: 0 0 34px 34px;
  background:
    radial-gradient(circle at 76% 20%, rgba(212, 175, 55, .24), transparent 28%),
    radial-gradient(circle at 20% 80%, rgba(37, 99, 235, .28), transparent 40%),
    linear-gradient(145deg, #05070d 0%, #09111f 50%, #111827 100%);
  border: 1px solid rgba(255, 255, 255, .08);
  border-top: none;
  box-shadow: 0 26px 72px rgba(0, 0, 0, .48);
}

.hero-grid-bg {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, .032) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .032) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: .72;
}

.hero-glow-gold,
.hero-glow-blue {
  position: absolute;
  border-radius: 50%;
  filter: blur(48px);
}

.hero-glow-gold {
  width: 220px;
  height: 220px;
  right: -90px;
  top: 80px;
  background: rgba(212, 175, 55, .26);
}

.hero-glow-blue {
  width: 240px;
  height: 240px;
  left: -100px;
  bottom: -90px;
  background: rgba(37, 99, 235, .26);
}

.hero-content {
  position: relative;
  z-index: 5;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .65px;
  color: #ffe08a;
  background: rgba(212, 175, 55, .12);
  border: 1px solid rgba(212, 175, 55, .34);
}

.hero-badge i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #facc15;
  box-shadow: 0 0 14px rgba(250, 204, 21, .9);
}

.hero-premium h1 {
  margin: 22px 0 0;
  max-width: 760px;
  font-size: 39px;
  line-height: 1.08;
  font-weight: 950;
  letter-spacing: -1.5px;
  color: #fff;
}

.hero-premium h1 span {
  display: block;
  margin-top: 6px;
  background: linear-gradient(90deg, #d4af37 0%, #ffe08a 54%, #ffffff 100%);
  -webkit-background-clip: text;
  color: transparent;
}

.hero-slogan {
  margin-top: 18px;
  font-size: 15px;
  line-height: 1.85;
  font-weight: 900;
  color: #f5f7ff;
}

.hero-desc {
  max-width: 720px;
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.95;
  color: #9fb3d7;
}

.arbitrage-orbit {
  position: absolute;
  z-index: 2;
  right: 28px;
  top: 34px;
  width: 142px;
  height: 142px;
  pointer-events: none;
  opacity: .92;
}

.orbit-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(212, 175, 55, .26);
}

.ring-a {
  animation: orbitRotate 14s linear infinite;
}

.ring-b {
  inset: 18px;
  border-color: rgba(59, 130, 246, .25);
  animation: orbitRotateReverse 10s linear infinite;
}

.ring-c {
  inset: 36px;
  border-color: rgba(255, 255, 255, .12);
}

.orbit-core {
  position: absolute;
  inset: 47px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #080b12;
  font-size: 33px;
  font-weight: 950;
  background:
    radial-gradient(circle at 28% 20%, #fff5c7, transparent 25%),
    linear-gradient(135deg, #d4af37, #ffe08a);
  box-shadow: 0 0 32px rgba(212, 175, 55, .48);
  animation: btcFloat 4.8s ease-in-out infinite;
}

.orbit-dot {
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.dot-a {
  right: 8px;
  top: 44px;
  background: #d4af37;
  box-shadow: 0 0 16px rgba(212, 175, 55, .9);
  animation: dotPulse 2.6s ease-in-out infinite;
}

.dot-b {
  left: 18px;
  bottom: 30px;
  background: #3b82f6;
  box-shadow: 0 0 16px rgba(59, 130, 246, .9);
  animation: dotPulse 2.6s ease-in-out infinite .8s;
}

@keyframes orbitRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes orbitRotateReverse {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}

@keyframes btcFloat {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-7px) scale(1.04); }
}

@keyframes dotPulse {
  0%, 100% { transform: scale(.9); opacity: .6; }
  50% { transform: scale(1.25); opacity: 1; }
}

.hero-model {
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.hero-model > div,
.section-card,
.order-card {
  background: linear-gradient(180deg, rgba(18, 28, 48, .88), rgba(8, 14, 26, .92));
  border: 1px solid rgba(148, 163, 184, .15);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .06), 0 18px 48px rgba(0, 0, 0, .26);
}

.hero-model > div {
  padding: 15px 14px;
  border-radius: 18px;
}

.hero-model > div:last-child {
  grid-column: 1 / -1;
}

.hero-model span {
  display: block;
  color: #8ea4c8;
  font-size: 12px;
}

.hero-model strong {
  display: block;
  margin-top: 8px;
  color: #ffe08a;
  font-size: 32px;
}

.hero-model small {
  display: block;
  margin-top: 4px;
  color: #64799d;
  font-size: 10px;
  letter-spacing: .35px;
}

.section-card {
  margin-top: 14px;
  border-radius: 28px;
  padding: 18px 14px;
}

.section-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.section-head h2 {
  margin: 0;
  font-size: 22px;
}

.section-head p {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: #8ea4c8;
}

.section-head > span {
  flex-shrink: 0;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, .12);
  border: 1px solid rgba(212, 175, 55, .18);
  font-size: 10px;
  font-weight: 950;
  letter-spacing: .4px;
}

.order-mode-grid {
  margin-top: 18px;
  display: grid;
  gap: 12px;
}

.order-mode-card {
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 16px 14px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  gap: 13px;
  color: #fff;
  text-align: left;
  cursor: pointer;
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .08);
  transition: border-color .22s ease, transform .22s ease, background .22s ease;
}

.order-mode-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity .22s ease;
}

.order-mode-card.standard::before {
  background: linear-gradient(125deg, rgba(212, 175, 55, .14), transparent 62%);
}

.order-mode-card.trial::before {
  background: linear-gradient(125deg, rgba(37, 99, 235, .17), transparent 62%);
}

.order-mode-card.active,
.order-mode-card:hover {
  transform: translateY(-1px);
}

.order-mode-card.active::before {
  opacity: 1;
}

.order-mode-card.standard.active {
  border-color: rgba(212, 175, 55, .5);
  box-shadow: 0 14px 34px rgba(212, 175, 55, .09), inset 0 0 0 1px rgba(212, 175, 55, .08);
}

.order-mode-card.trial.active {
  border-color: rgba(59, 130, 246, .55);
  box-shadow: 0 14px 34px rgba(37, 99, 235, .12), inset 0 0 0 1px rgba(59, 130, 246, .08);
}

.mode-check {
  position: absolute;
  right: 12px;
  top: 12px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, .18);
}

.order-mode-card.active .mode-check::after {
  content: '';
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  background: #ffe08a;
  box-shadow: 0 0 10px rgba(255, 224, 138, .75);
}

.order-mode-card.trial.active .mode-check::after {
  background: #60a5fa;
  box-shadow: 0 0 10px rgba(96, 165, 250, .75);
}

.mode-icon {
  position: relative;
  z-index: 1;
  flex: 0 0 52px;
  width: 52px;
  height: 52px;
  border-radius: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 950;
  color: #080b12;
  background: linear-gradient(135deg, #d4af37, #ffe08a);
  box-shadow: 0 12px 28px rgba(212, 175, 55, .22);
}

.order-mode-card.trial .mode-icon {
  color: #fff;
  font-size: 15px;
  letter-spacing: -.3px;
  background: linear-gradient(135deg, #2563eb, #60a5fa);
  box-shadow: 0 12px 28px rgba(37, 99, 235, .24);
}

.mode-copy {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
}

.mode-topline {
  padding-right: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.mode-topline b {
  font-size: 16px;
}

.mode-topline em {
  padding: 4px 7px;
  border-radius: 999px;
  color: #ffe08a;
  background: rgba(212, 175, 55, .12);
  font-size: 9px;
  font-weight: 900;
  font-style: normal;
}

.order-mode-card.trial .mode-topline em {
  color: #93c5fd;
  background: rgba(37, 99, 235, .16);
}

.mode-copy > strong,
.mode-copy > small {
  display: block;
}

.mode-copy > strong {
  margin-top: 7px;
  color: #dce8ff;
  font-size: 13px;
}

.mode-copy > small {
  margin-top: 5px;
  color: #7186aa;
  font-size: 10px;
  line-height: 1.55;
}

.selected-plan-bar {
  margin-top: 12px;
  padding: 13px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: rgba(212, 175, 55, .07);
  border: 1px solid rgba(212, 175, 55, .16);
}

.selected-plan-bar.trial {
  background: rgba(37, 99, 235, .08);
  border-color: rgba(59, 130, 246, .19);
}

.selected-plan-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.selected-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  margin-top: 5px;
  border-radius: 50%;
  background: #facc15;
  box-shadow: 0 0 12px rgba(250, 204, 21, .8);
}

.selected-plan-bar.trial .selected-dot {
  background: #60a5fa;
  box-shadow: 0 0 12px rgba(96, 165, 250, .8);
}

.selected-plan-left b {
  display: block;
  font-size: 12px;
}

.selected-plan-left p {
  margin: 5px 0 0;
  color: #7186aa;
  font-size: 10px;
  line-height: 1.55;
}

.selected-plan-code {
  display: none;
  flex-shrink: 0;
  color: #8ca0c1;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .7px;
}

.amount-box {
  margin-top: 18px;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.field-label-row label {
  color: #c7d6ef;
  font-size: 13px;
  font-weight: 850;
}

.field-label-row span {
  color: #64799d;
  font-size: 10px;
}

.amount-input {
  margin-top: 10px;
  height: 62px;
  border-radius: 19px;
  padding: 0 15px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, .055);
  border: 1px solid rgba(255, 255, 255, .1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .035);
  transition: border-color .2s ease, box-shadow .2s ease;
}

.amount-input:focus-within {
  border-color: rgba(212, 175, 55, .5);
  box-shadow: 0 0 0 3px rgba(212, 175, 55, .08);
}

.amount-input input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #fff;
  font-size: 23px;
  font-weight: 950;
}

.amount-input input::placeholder {
  color: #52627f;
}

.amount-input b {
  color: #ffe08a;
  font-size: 13px;
}

.quick-amounts {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.quick-amounts button {
  min-width: 0;
  height: 36px;
  padding: 0 5px;
  border-radius: 11px;
  color: #879abd;
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .07);
  font-size: 10px;
  font-weight: 850;
  cursor: pointer;
}

.quick-amounts button.active {
  color: #ffe08a;
  background: rgba(212, 175, 55, .12);
  border-color: rgba(212, 175, 55, .28);
}

.calc-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.calc-grid > div,
.order-data > div {
  min-width: 0;
  padding: 14px 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .07);
}

.calc-grid .active,
.order-data .highlight-data {
  background: rgba(212, 175, 55, .1);
  border-color: rgba(212, 175, 55, .24);
}

.calc-grid span,
.order-data span {
  display: block;
  color: #8ea4c8;
  font-size: 11px;
}

.calc-grid strong,
.order-data strong {
  display: block;
  overflow: hidden;
  margin-top: 8px;
  color: #f8fbff;
  font-size: 18px;
  text-overflow: ellipsis;
}

.calc-grid em,
.order-data em {
  display: block;
  margin-top: 5px;
  color: #ffe08a;
  font-style: normal;
  font-size: 9px;
  letter-spacing: .25px;
}

.principal-flow {
  margin-top: 14px;
  padding: 14px;
  border-radius: 19px;
  background: rgba(255, 255, 255, .035);
  border: 1px solid rgba(255, 255, 255, .065);
}

.flow-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.flow-head span {
  color: #8ea4c8;
  font-size: 11px;
}

.flow-head b {
  color: #dce8ff;
  font-size: 12px;
}

.flow-track {
  margin-top: 11px;
  height: 12px;
  overflow: hidden;
  display: flex;
  border-radius: 999px;
  background: rgba(255, 255, 255, .07);
}

.flow-track > div {
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width .35s ease;
}

.flow-track span {
  color: rgba(5, 7, 13, .8);
  font-size: 8px;
  font-weight: 950;
}

.flow-fee {
  background: linear-gradient(90deg, #8c6c1f, #d4af37);
}

.flow-pool {
  background: linear-gradient(90deg, #2563eb, #60a5fa);
}

.flow-legend {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
}

.flow-legend span {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #7186aa;
  font-size: 9px;
}

.flow-legend i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.flow-legend i.fee {
  background: #d4af37;
}

.flow-legend i.pool {
  background: #3b82f6;
}

.rule-note {
  margin-top: 14px;
  padding: 13px;
  border-radius: 17px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 11px;
  line-height: 1.75;
}

.rule-note p {
  margin: 0;
}

.standard-note {
  color: #d7c993;
  background: rgba(212, 175, 55, .08);
  border: 1px solid rgba(212, 175, 55, .19);
}

.trial-note {
  color: #a9c9f8;
  background: rgba(37, 99, 235, .09);
  border: 1px solid rgba(59, 130, 246, .2);
}

.note-icon {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #090b11;
  background: #ffe08a;
  font-size: 11px;
  font-weight: 950;
}

.trial-note .note-icon {
  color: #fff;
  background: #3b82f6;
}

.trial-product-card {
  position: relative;
  overflow: hidden;
  margin-top: 18px;
  padding: 16px 14px;
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0, rgba(59, 130, 246, .2), transparent 32%),
    linear-gradient(145deg, rgba(15, 35, 68, .82), rgba(8, 16, 31, .96));
  border: 1px solid rgba(59, 130, 246, .24);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .05);
}

.trial-product-card::after {
  content: '7D';
  position: absolute;
  right: -4px;
  top: 54px;
  color: rgba(255, 255, 255, .025);
  font-size: 112px;
  line-height: 1;
  font-weight: 950;
  pointer-events: none;
}

.trial-product-head {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.trial-label {
  display: inline-flex;
  color: #93c5fd;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: 1px;
}

.trial-product-head h3 {
  margin: 7px 0 0;
  font-size: 21px;
}

.trial-product-head p {
  margin: 7px 0 0;
  max-width: 520px;
  color: #7990b5;
  font-size: 11px;
  line-height: 1.65;
}

.trial-amount {
  flex-shrink: 0;
  padding: 13px 14px;
  border-radius: 18px;
  background: rgba(59, 130, 246, .12);
  border: 1px solid rgba(96, 165, 250, .2);
}

.trial-amount span,
.trial-amount strong,
.trial-amount em {
  display: block;
}

.trial-amount span {
  color: #8eacd8;
  font-size: 10px;
}

.trial-amount strong {
  margin-top: 5px;
  color: #fff;
  font-size: 27px;
}

.trial-amount em {
  margin-top: 2px;
  color: #60a5fa;
  font-size: 10px;
  font-style: normal;
  font-weight: 900;
}

.trial-feature-grid {
  position: relative;
  z-index: 1;
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.trial-feature-grid > div {
  padding: 13px 11px;
  border-radius: 17px;
  background: rgba(255, 255, 255, .04);
  border: 1px solid rgba(255, 255, 255, .065);
}

.trial-feature-grid span,
.trial-feature-grid strong,
.trial-feature-grid em {
  display: block;
}

.trial-feature-grid span {
  color: #7f96bb;
  font-size: 10px;
}

.trial-feature-grid strong {
  margin-top: 7px;
  font-size: 17px;
}

.trial-feature-grid em {
  margin-top: 4px;
  color: #60a5fa;
  font-size: 9px;
  font-style: normal;
}

.trial-quota-panel {
  position: relative;
  z-index: 1;
  margin-top: 14px;
  padding: 13px;
  border-radius: 18px;
  background: rgba(4, 10, 22, .38);
  border: 1px solid rgba(96, 165, 250, .12);
}

.quota-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.quota-title > div {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.quota-title span {
  color: #8ea4c8;
  font-size: 10px;
}

.quota-title b {
  font-size: 13px;
}

.quota-title em {
  padding: 4px 7px;
  border-radius: 999px;
  color: #93c5fd;
  background: rgba(59, 130, 246, .13);
  font-size: 8px;
  font-style: normal;
  font-weight: 900;
}

.quota-line {
  margin-top: 10px;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, .07);
}

.quota-line div {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #2563eb, #60a5fa);
  box-shadow: 0 0 15px rgba(59, 130, 246, .38);
}

.eligibility-grid {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}

.eligibility-item {
  padding: 10px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 9px;
  background: rgba(239, 68, 68, .055);
  border: 1px solid rgba(239, 68, 68, .12);
}

.eligibility-item.passed {
  background: rgba(34, 197, 94, .055);
  border-color: rgba(34, 197, 94, .12);
}

.eligibility-item > i {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fecaca;
  background: rgba(239, 68, 68, .13);
  font-size: 10px;
  font-style: normal;
  font-weight: 950;
}

.eligibility-item.passed > i {
  color: #bbf7d0;
  background: rgba(34, 197, 94, .13);
}

.eligibility-item span,
.eligibility-item b,
.eligibility-item small {
  display: block;
}

.eligibility-item b {
  font-size: 10px;
}

.eligibility-item small {
  margin-top: 3px;
  color: #7186aa;
  font-size: 8px;
}

.primary-btn {
  margin-top: 16px;
  width: 100%;
  min-height: 56px;
  padding: 9px 14px;
  border: none;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #080b12;
  background: linear-gradient(135deg, #d4af37 0%, #ffe08a 100%);
  box-shadow: 0 16px 36px rgba(212, 175, 55, .25);
  cursor: pointer;
  transition: transform .2s ease, opacity .2s ease, box-shadow .2s ease;
}

.primary-btn.trial {
  color: #fff;
  background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 52%, #60a5fa 100%);
  box-shadow: 0 16px 36px rgba(37, 99, 235, .25);
}

.primary-btn:not(:disabled):active {
  transform: translateY(1px) scale(.995);
}

.primary-btn:disabled {
  opacity: .48;
  cursor: not-allowed;
  box-shadow: none;
}

.primary-btn span {
  font-size: 14px;
  font-weight: 950;
}

.primary-btn small {
  margin-top: 3px;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: .6px;
  opacity: .65;
}

.portfolio-card {
  overflow: hidden;
}

.order-filter {
  margin-top: 16px;
  padding: 5px;
  border-radius: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  background: rgba(255, 255, 255, .04);
  border: 1px solid rgba(255, 255, 255, .06);
}

.order-filter button {
  min-width: 0;
  height: 40px;
  padding: 0 5px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #7186aa;
  background: transparent;
  border: none;
  font-size: 10px;
  font-weight: 850;
  cursor: pointer;
}

.order-filter button span {
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #7990b5;
  background: rgba(255, 255, 255, .06);
  font-size: 8px;
}

.order-filter button.active {
  color: #fff;
  background: rgba(255, 255, 255, .075);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .04), 0 6px 14px rgba(0, 0, 0, .12);
}

.order-filter button.active span {
  color: #ffe08a;
  background: rgba(212, 175, 55, .12);
}

.empty-box {
  margin-top: 16px;
  padding: 34px 20px;
  border-radius: 20px;
  text-align: center;
  color: #8ea4c8;
  background: rgba(255, 255, 255, .035);
  border: 1px dashed rgba(255, 255, 255, .08);
}

.empty-icon {
  width: 46px;
  height: 46px;
  margin: 0 auto 11px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, .1);
  border: 1px solid rgba(212, 175, 55, .17);
  font-size: 22px;
}

.empty-box b {
  display: block;
  color: #dce8ff;
  font-size: 13px;
}

.empty-box p {
  margin: 7px 0 0;
  font-size: 10px;
}

.order-list {
  margin-top: 14px;
  display: grid;
  gap: 14px;
}

.order-card {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 17px 14px 15px;
}

.order-card.trial-order {
  background:
    radial-gradient(circle at 100% 0, rgba(59, 130, 246, .1), transparent 32%),
    linear-gradient(180deg, rgba(16, 31, 55, .9), rgba(8, 14, 26, .95));
  border-color: rgba(59, 130, 246, .18);
}

.order-accent {
  position: absolute;
  left: 0;
  top: 22px;
  bottom: 22px;
  width: 3px;
  border-radius: 0 999px 999px 0;
  background: linear-gradient(180deg, #d4af37, rgba(212, 175, 55, .12));
}

.trial-order .order-accent {
  background: linear-gradient(180deg, #3b82f6, rgba(59, 130, 246, .12));
}

.order-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.order-identity {
  min-width: 0;
}

.order-type-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.order-type-badge,
.order-rule-chip {
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  font-size: 8px;
  font-weight: 950;
  letter-spacing: .2px;
}

.order-type-badge.standard {
  color: #ffe08a;
  background: rgba(212, 175, 55, .11);
  border: 1px solid rgba(212, 175, 55, .19);
}

.order-type-badge.trial {
  color: #93c5fd;
  background: rgba(59, 130, 246, .11);
  border: 1px solid rgba(59, 130, 246, .19);
}

.order-rule-chip {
  color: #7186aa;
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .06);
}

.order-top h3 {
  overflow: hidden;
  margin: 10px 0 0;
  color: #f3f7ff;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-top p {
  margin: 5px 0 0;
  color: #64799d;
  font-size: 9px;
}

.status {
  flex-shrink: 0;
  height: 28px;
  padding: 0 9px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  font-weight: 900;
}

.status i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 8px currentColor;
}

.status.running {
  color: #4ade80;
  background: rgba(34, 197, 94, .1);
}

.status.redeeming {
  color: #ffe08a;
  background: rgba(212, 175, 55, .1);
}

.status.matured {
  color: #93c5fd;
  background: rgba(59, 130, 246, .12);
}

.status.pending,
.status.stopped {
  color: #cbd5e1;
  background: rgba(148, 163, 184, .1);
}

.status.finished {
  color: #60a5fa;
  background: rgba(96, 165, 250, .11);
}

.status.cancelled,
.status.failed {
  color: #fca5a5;
  background: rgba(239, 68, 68, .1);
}

.order-data {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.trial-order .order-data .highlight-data {
  background: rgba(59, 130, 246, .09);
  border-color: rgba(59, 130, 246, .2);
}

.trial-order .order-data em {
  color: #60a5fa;
}

.order-data .date-value {
  font-size: 14px;
}

.progress-box {
  margin-top: 14px;
  padding: 12px;
  border-radius: 17px;
  background: rgba(255, 255, 255, .03);
  border: 1px solid rgba(255, 255, 255, .055);
}

.progress-row {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #8ea4c8;
}

.progress-row b {
  color: #ffe08a;
}

.trial-progress-box .progress-row b {
  color: #60a5fa;
}

.progress-line {
  margin-top: 8px;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, .07);
}

.progress-line > div {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #d4af37, #ffe08a);
  box-shadow: 0 0 12px rgba(212, 175, 55, .24);
}

.progress-line.trial-progress > div {
  background: linear-gradient(90deg, #2563eb, #60a5fa);
  box-shadow: 0 0 12px rgba(59, 130, 246, .26);
}

.progress-meta {
  margin-top: 9px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 5px 9px;
}

.progress-meta span {
  color: #607496;
  font-size: 8px;
}

.order-actions {
  margin-top: 13px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.order-actions button {
  min-width: 0;
  height: 42px;
  padding: 0 8px;
  border-radius: 14px;
  color: #dbeafe;
  background: rgba(255, 255, 255, .06);
  border: 1px solid rgba(255, 255, 255, .085);
  font-size: 10px;
  font-weight: 900;
  cursor: pointer;
}

.order-actions button.gold {
  color: #080b12;
  background: linear-gradient(135deg, #d4af37, #ffe08a);
  border-color: transparent;
}

.order-actions button.trial-action.ready {
  color: #fff;
  background: linear-gradient(135deg, #1d4ed8, #60a5fa);
  border-color: transparent;
}

.order-actions button:disabled {
  color: #52627f;
  background: rgba(255, 255, 255, .035);
  border-color: rgba(255, 255, 255, .045);
  cursor: not-allowed;
}


.order-mode-card:disabled,
.quick-amounts button:disabled,
.amount-input input:disabled {
  cursor: not-allowed;
}

.order-mode-card:disabled,
.quick-amounts button:disabled {
  opacity: .62;
}

.order-process-panel {
  margin-top: 16px;
  padding: 15px 14px;
  border-radius: 20px;
  background:
    radial-gradient(circle at 100% 0, rgba(59, 130, 246, .12), transparent 38%),
    rgba(5, 12, 24, .64);
  border: 1px solid rgba(96, 165, 250, .16);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .035);
}

.order-process-panel.success {
  border-color: rgba(34, 197, 94, .22);
  background:
    radial-gradient(circle at 100% 0, rgba(34, 197, 94, .11), transparent 38%),
    rgba(5, 18, 18, .65);
}

.order-process-panel.error {
  border-color: rgba(239, 68, 68, .24);
  background:
    radial-gradient(circle at 100% 0, rgba(239, 68, 68, .1), transparent 38%),
    rgba(22, 8, 13, .66);
}

.process-head,
.process-head-left {
  display: flex;
  align-items: flex-start;
}

.process-head {
  justify-content: space-between;
  gap: 12px;
}

.process-head-left {
  min-width: 0;
  gap: 10px;
}

.process-state-icon {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bfdbfe;
  background: rgba(59, 130, 246, .12);
  border: 1px solid rgba(96, 165, 250, .2);
}

.process-state-icon.success {
  color: #bbf7d0;
  background: rgba(34, 197, 94, .12);
  border-color: rgba(34, 197, 94, .2);
}

.process-state-icon.error {
  color: #fecaca;
  background: rgba(239, 68, 68, .12);
  border-color: rgba(239, 68, 68, .22);
}

.process-state-icon i,
.btn-spinner {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid rgba(147, 197, 253, .24);
  border-top-color: #93c5fd;
  animation: orderSpin .72s linear infinite;
}

@keyframes orderSpin {
  to { transform: rotate(360deg); }
}

.process-head-left strong,
.process-head-left p {
  display: block;
}

.process-head-left strong {
  color: #eef5ff;
  font-size: 12px;
}

.process-head-left p {
  margin: 5px 0 0;
  color: #7890b5;
  font-size: 9px;
  line-height: 1.6;
}

.process-plan {
  flex-shrink: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  color: #93c5fd;
  background: rgba(59, 130, 246, .1);
  border: 1px solid rgba(96, 165, 250, .14);
  font-size: 8px;
  font-weight: 900;
  letter-spacing: .5px;
}

.process-track {
  margin-top: 13px;
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, .07);
}

.process-track > div {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #2563eb, #60a5fa);
  box-shadow: 0 0 12px rgba(59, 130, 246, .3);
  transition: width .3s ease;
}

.order-process-panel.success .process-track > div {
  background: linear-gradient(90deg, #16a34a, #4ade80);
}

.order-process-panel.error .process-track > div {
  background: linear-gradient(90deg, #b91c1c, #f87171);
}

.process-steps {
  margin-top: 13px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
}

.process-step {
  min-width: 0;
  padding: 9px 7px;
  border-radius: 13px;
  display: flex;
  align-items: flex-start;
  gap: 7px;
  background: rgba(255, 255, 255, .025);
  border: 1px solid rgba(255, 255, 255, .05);
}

.process-step > span {
  flex: 0 0 19px;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5f7394;
  background: rgba(255, 255, 255, .055);
  font-size: 8px;
  font-weight: 900;
}

.process-step b,
.process-step small {
  display: block;
}

.process-step b {
  color: #7186aa;
  font-size: 8px;
}

.process-step small {
  margin-top: 3px;
  color: #526583;
  font-size: 7px;
  line-height: 1.35;
}

.process-step.active {
  background: rgba(59, 130, 246, .08);
  border-color: rgba(96, 165, 250, .16);
}

.process-step.active > span {
  color: #fff;
  background: #3b82f6;
  box-shadow: 0 0 10px rgba(59, 130, 246, .38);
}

.process-step.active b {
  color: #dbeafe;
}

.process-step.done {
  background: rgba(34, 197, 94, .055);
  border-color: rgba(34, 197, 94, .12);
}

.process-step.done > span {
  color: #052e16;
  background: #4ade80;
}

.process-step.done b {
  color: #bbf7d0;
}

.process-step.error {
  background: rgba(239, 68, 68, .06);
  border-color: rgba(239, 68, 68, .14);
}

.process-step.error > span {
  color: #fff;
  background: #ef4444;
}

.process-step.error b {
  color: #fecaca;
}

.process-chain-data {
  margin-top: 12px;
  display: grid;
  gap: 6px;
}

.process-chain-data > div {
  padding: 8px 9px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: rgba(255, 255, 255, .025);
  border: 1px solid rgba(255, 255, 255, .045);
}

.process-chain-data span {
  color: #607496;
  font-size: 8px;
}

.process-chain-data strong {
  overflow: hidden;
  color: #b9ccef;
  font-family: monospace;
  font-size: 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.process-success-note {
  margin-top: 11px;
  padding: 10px;
  border-radius: 12px;
  color: #a7f3d0;
  background: rgba(34, 197, 94, .07);
  border: 1px solid rgba(34, 197, 94, .12);
  font-size: 8px;
  line-height: 1.55;
}

.primary-btn.loading {
  opacity: .86;
  cursor: wait;
}

.primary-btn .btn-spinner {
  margin-bottom: 5px;
  border-color: rgba(8, 11, 18, .22);
  border-top-color: #080b12;
}

.primary-btn.trial .btn-spinner {
  border-color: rgba(255, 255, 255, .25);
  border-top-color: #fff;
}

@media (min-width: 768px) {
  .page-body {
    max-width: 1180px;
    margin: 0 auto;
  }

  .hero-premium {
    min-height: 640px;
    padding: 52px 44px 38px;
  }

  .hero-premium h1 {
    font-size: 60px;
  }

  .hero-desc {
    font-size: 15px;
  }

  .arbitrage-orbit {
    width: 220px;
    height: 220px;
    right: 70px;
    top: 64px;
  }

  .orbit-core {
    inset: 74px;
    font-size: 52px;
  }

  .ring-b {
    inset: 28px;
  }

  .ring-c {
    inset: 56px;
  }

  .hero-model {
    max-width: 770px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .hero-model > div:last-child {
    grid-column: auto;
  }

  .section-card {
    padding: 24px;
  }

  .section-head h2 {
    font-size: 25px;
  }

  .order-mode-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .order-mode-card {
    min-height: 116px;
    padding: 18px;
  }

  .selected-plan-code {
    display: block;
  }

  .calc-grid,
  .order-data,
  .trial-feature-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .trial-product-head {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .trial-amount {
    min-width: 185px;
  }

  .eligibility-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .order-filter {
    max-width: 520px;
  }

  .order-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .order-card {
    padding: 20px 18px 17px;
  }
}

@media (min-width: 1080px) {
  .deposit-card,
  .portfolio-card {
    border-radius: 30px;
  }

  .order-mode-card {
    padding: 20px;
  }

  .mode-icon {
    flex-basis: 58px;
    width: 58px;
    height: 58px;
  }

  .order-top h3 {
    font-size: 15px;
  }
}

.system-notice-card .retry-btn,
.retry-btn {
  margin-top: 14px;
  width: 100%;
  height: 42px;
  border: 1px solid rgba(212, 175, 55, .22);
  border-radius: 14px;
  color: #ffe08a;
  background: rgba(212, 175, 55, .08);
  font-size: 12px;
  font-weight: 900;
}

.error-card {
  border-color: rgba(239, 68, 68, .22);
}

.load-more-area {
  margin-top: 16px;
  text-align: center;
}

.load-more-area p {
  margin: 0;
  color: #607496;
  font-size: 10px;
}

.load-more-btn-dark {
  width: 100%;
  height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, .08);
  color: #dbeafe;
  background: rgba(255, 255, 255, .055);
  font-size: 11px;
  font-weight: 900;
}

.load-more-btn-dark:disabled {
  opacity: .55;
}



/* =========================================================
 * PROFESSIONAL STRATEGY HUB VISUAL UPGRADE
 * 深石墨黑 + 电光蓝 + 冷紫 + 少量金色
 * ========================================================= */

.invest-page {
  position: relative;
  isolation: isolate;
  overflow-x: hidden;

  background:
    radial-gradient(circle at 12% 7%, rgba(34, 211, 238, .085), transparent 24%),
    radial-gradient(circle at 91% 20%, rgba(124, 58, 237, .11), transparent 28%),
    radial-gradient(circle at 50% 78%, rgba(212, 175, 55, .055), transparent 34%),
    linear-gradient(180deg, #03050a 0%, #070b13 34%, #090d18 70%, #060910 100%);
}

.invest-page::before {
  content: '';
  position: fixed;
  z-index: -2;
  inset: 0;
  pointer-events: none;

  background:
    linear-gradient(115deg, transparent 0 46%, rgba(34, 211, 238, .025) 49%, transparent 52%),
    radial-gradient(circle at 50% 0%, rgba(255,255,255,.025), transparent 28%);
  background-size: 180% 180%, auto;
  animation: strategyPageAmbient 16s ease-in-out infinite;
}

.invest-page::after {
  content: '';
  position: fixed;
  z-index: -1;
  inset: 0;
  pointer-events: none;
  opacity: .16;

  background-image:
    radial-gradient(circle, rgba(255,255,255,.2) .55px, transparent .75px);
  background-size: 11px 11px;
  mask-image: linear-gradient(180deg, rgba(0,0,0,.8), transparent 72%);
}

.page-body {
  position: relative;
  z-index: 1;
}

/* Hero */
.hero-premium {
  min-height: 590px;
  border-radius: 0 0 38px 38px;

  background:
    radial-gradient(circle at 78% 14%, rgba(124, 58, 237, .24), transparent 26%),
    radial-gradient(circle at 12% 82%, rgba(6, 182, 212, .2), transparent 34%),
    radial-gradient(circle at 54% 115%, rgba(212, 175, 55, .12), transparent 34%),
    linear-gradient(145deg, #03050a 0%, #09101d 48%, #11101f 100%);

  border-color: rgba(103, 232, 249, .12);

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.075),
    inset 0 -1px 0 rgba(103,232,249,.055),
    0 32px 90px rgba(0,0,0,.58),
    0 0 54px rgba(76,29,149,.07);
}

.hero-premium::before {
  content: '';
  position: absolute;
  z-index: 1;
  inset: 0;
  pointer-events: none;

  background:
    linear-gradient(180deg, rgba(255,255,255,.025), transparent 18%),
    radial-gradient(circle at center, transparent 46%, rgba(0,0,0,.28) 100%);
}

.hero-premium::after {
  content: '';
  position: absolute;
  z-index: 7;
  left: 8%;
  right: 8%;
  bottom: 0;
  height: 2px;
  pointer-events: none;

  background: linear-gradient(
    90deg,
    transparent,
    #22d3ee 20%,
    #8b5cf6 44%,
    #ffe08a 58%,
    #8b5cf6 76%,
    transparent
  );
  background-size: 220% 100%;
  box-shadow: 0 0 18px rgba(34,211,238,.35);
  animation: strategyHeroEdge 6s linear infinite;
}

.hero-grid-bg {
  z-index: 0;
  opacity: .5;
  background-image:
    linear-gradient(rgba(103,232,249,.036) 1px, transparent 1px),
    linear-gradient(90deg, rgba(167,139,250,.028) 1px, transparent 1px);
  background-size: 32px 32px;
  animation: strategyHeroGrid 22s linear infinite;
}

.hero-glow-gold {
  animation: strategyGlowPulse 7s ease-in-out infinite;
}

.hero-glow-blue {
  background: rgba(6,182,212,.24);
  animation: strategyGlowPulse 8.5s ease-in-out infinite reverse;
}

.hero-scan-beam {
  position: absolute;
  z-index: 2;
  top: -120px;
  left: -170px;
  width: 180px;
  height: 760px;
  pointer-events: none;

  transform: rotate(22deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255,255,255,.07),
    rgba(34,211,238,.09),
    transparent
  );
  filter: blur(2px);
  animation: strategyHeroScan 10s ease-in-out infinite;
}

.hero-data-stream {
  position: absolute;
  z-index: 1;
  right: 0;
  top: 0;
  width: 42%;
  height: 100%;
  pointer-events: none;
  opacity: .35;

  background:
    repeating-linear-gradient(
      180deg,
      transparent 0,
      transparent 19px,
      rgba(103,232,249,.06) 20px,
      transparent 21px
    );
  mask-image: linear-gradient(90deg, transparent, #000);
  animation: strategyDataStream 8s linear infinite;
}

.hero-content {
  z-index: 5;
}

.hero-badge {
  color: #bff7ff;
  border-color: rgba(103,232,249,.26);
  background: linear-gradient(135deg, rgba(6,182,212,.11), rgba(124,58,237,.08));
  box-shadow: inset 0 1px 0 rgba(255,255,255,.045);
}

.hero-badge i {
  background: #22d3ee;
  box-shadow: 0 0 15px rgba(34,211,238,.95);
  animation: strategyPulseDot 1.7s ease-in-out infinite;
}

.hero-system-rail {
  margin-top: 13px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
}

.hero-system-rail > span {
  min-height: 25px;
  padding: 0 9px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  color: #7184a7;
  background: rgba(255,255,255,.035);
  border: 1px solid rgba(255,255,255,.055);

  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 7px;
  font-weight: 900;
  letter-spacing: .45px;
}

.hero-system-rail .rail-online {
  color: #86efac;
  border-color: rgba(34,197,94,.13);
  background: rgba(34,197,94,.055);
}

.hero-system-rail .rail-online i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 9px rgba(34,197,94,.9);
  animation: strategyPulseDot 1.7s ease-in-out infinite;
}

.hero-premium h1 {
  text-shadow: 0 12px 42px rgba(0,0,0,.44);
}

.hero-premium h1 span {
  background:
    linear-gradient(90deg, #67e8f9 0%, #a78bfa 42%, #ffe08a 75%, #ffffff 100%);
  background-size: 190% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: strategyTitleGradient 7s linear infinite;
}

.hero-slogan {
  color: #edf3ff;
  text-shadow: 0 4px 18px rgba(0,0,0,.25);
}

.hero-desc {
  color: #9aaaca;
}

/* Orbit */
.arbitrage-orbit {
  filter: drop-shadow(0 0 28px rgba(124,58,237,.14));
}

.orbit-ring {
  border-color: rgba(103,232,249,.22);
  box-shadow: inset 0 0 20px rgba(34,211,238,.035);
}

.ring-b {
  border-color: rgba(167,139,250,.25);
}

.ring-c {
  border-color: rgba(255,224,138,.14);
}

.orbit-core {
  background:
    radial-gradient(circle at 28% 20%, #ffffff, transparent 23%),
    linear-gradient(135deg, #67e8f9 0%, #a78bfa 48%, #ffe08a 100%);
  box-shadow:
    0 0 30px rgba(103,232,249,.26),
    0 0 48px rgba(124,58,237,.16);
}

.dot-a {
  background: #67e8f9;
  box-shadow: 0 0 17px rgba(103,232,249,.95);
}

.dot-b {
  background: #a78bfa;
  box-shadow: 0 0 17px rgba(167,139,250,.95);
}

/* Hero metrics */
.hero-model > div {
  position: relative;
  overflow: hidden;

  background:
    linear-gradient(155deg, rgba(23,29,48,.86), rgba(6,10,18,.92));
  border-color: rgba(103,232,249,.11);

  transition:
    transform .22s ease,
    border-color .22s ease,
    box-shadow .22s ease;
}

.hero-model > div::before {
  content: '';
  position: absolute;
  top: 0;
  left: 15%;
  right: 15%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(103,232,249,.54), transparent);
}

.hero-model > div::after {
  content: '';
  position: absolute;
  top: -30px;
  bottom: -30px;
  left: -44px;
  width: 28px;
  transform: skewX(-20deg);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.1), transparent);
  animation: strategyCardShine 7s ease-in-out infinite;
}

.hero-model > div:nth-child(2)::after {
  animation-delay: 1.4s;
}

.hero-model > div:nth-child(3)::after {
  animation-delay: 2.8s;
}

.hero-model > div:hover {
  transform: translateY(-4px);
  border-color: rgba(103,232,249,.24);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.07),
    0 18px 40px rgba(0,0,0,.34),
    0 0 28px rgba(34,211,238,.06);
}

.hero-model strong {
  color: #eaf9ff;
  text-shadow: 0 0 22px rgba(103,232,249,.16);
}

/* Main cards */
.section-card {
  position: relative;
  overflow: hidden;

  background:
    radial-gradient(circle at 100% 0%, rgba(124,58,237,.07), transparent 30%),
    linear-gradient(165deg, rgba(16,22,37,.96), rgba(5,9,16,.98));

  border-color: rgba(148,163,184,.12);

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.055),
    0 22px 58px rgba(0,0,0,.34);
}

.section-card::before {
  content: '';
  position: absolute;
  z-index: 0;
  top: 0;
  left: 18%;
  right: 18%;
  height: 1px;
  pointer-events: none;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(103,232,249,.32),
    rgba(167,139,250,.28),
    transparent
  );
}

.section-card > * {
  position: relative;
  z-index: 1;
}

.deposit-card {
  background:
    radial-gradient(circle at 100% 0%, rgba(6,182,212,.08), transparent 32%),
    radial-gradient(circle at 0% 100%, rgba(124,58,237,.06), transparent 34%),
    linear-gradient(160deg, rgba(13,22,35,.97), rgba(4,8,15,.99));
}

.portfolio-card {
  background:
    radial-gradient(circle at 98% 4%, rgba(124,58,237,.11), transparent 28%),
    radial-gradient(circle at 4% 96%, rgba(6,182,212,.07), transparent 31%),
    linear-gradient(155deg, rgba(12,12,24,.98), rgba(4,7,13,.995));
  border-color: rgba(167,139,250,.13);
}

.section-head h2 {
  color: #f5f8ff;
  letter-spacing: -.25px;
}

.section-head p {
  color: #8496b7;
}

.section-head > span {
  color: #bff7ff;
  background: rgba(6,182,212,.075);
  border-color: rgba(103,232,249,.15);
}

/* Strategy options */
.order-mode-card {
  min-height: 106px;

  background:
    linear-gradient(155deg, rgba(255,255,255,.055), rgba(255,255,255,.018));
  border-color: rgba(255,255,255,.075);

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.045),
    0 12px 30px rgba(0,0,0,.18);
}

.order-mode-card::after {
  content: '';
  position: absolute;
  z-index: 0;
  top: -40px;
  bottom: -40px;
  left: -52px;
  width: 34px;

  transform: skewX(-22deg);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.13), transparent);
  animation: strategyOptionShine 8s ease-in-out infinite;
}

.order-mode-card.trial::after {
  animation-delay: 2.2s;
}

.order-mode-card.standard.active {
  border-color: rgba(255,224,138,.38);
  background:
    radial-gradient(circle at 0% 50%, rgba(212,175,55,.13), transparent 46%),
    linear-gradient(155deg, rgba(36,29,15,.82), rgba(8,12,20,.94));
}

.order-mode-card.trial.active {
  border-color: rgba(103,232,249,.34);
  background:
    radial-gradient(circle at 0% 50%, rgba(6,182,212,.14), transparent 46%),
    linear-gradient(155deg, rgba(8,30,40,.84), rgba(8,10,23,.94));
}

.order-mode-card.trial .mode-icon {
  background: linear-gradient(135deg, #0891b2, #6366f1);
  box-shadow:
    0 12px 28px rgba(6,182,212,.18),
    0 0 22px rgba(99,102,241,.12);
}

.selected-plan-bar {
  position: relative;
  overflow: hidden;

  background:
    linear-gradient(135deg, rgba(212,175,55,.075), rgba(255,255,255,.02));
}

.selected-plan-bar.trial {
  background:
    linear-gradient(135deg, rgba(6,182,212,.08), rgba(124,58,237,.04));
  border-color: rgba(103,232,249,.16);
}

.selected-plan-bar::after {
  content: '';
  position: absolute;
  left: -80px;
  top: 0;
  width: 80px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #ffe08a, transparent);
  animation: strategySelectedFlow 5s linear infinite;
}

.selected-plan-bar.trial::after {
  background: linear-gradient(90deg, transparent, #67e8f9, transparent);
}

/* Inputs and calculations */
.amount-input {
  background:
    linear-gradient(145deg, rgba(255,255,255,.055), rgba(255,255,255,.022));
}

.amount-input:focus-within {
  border-color: rgba(103,232,249,.38);
  box-shadow:
    0 0 0 3px rgba(34,211,238,.065),
    0 0 24px rgba(34,211,238,.05);
}

.amount-input b {
  color: #67e8f9;
}

.quick-amounts button {
  transition:
    transform .18s ease,
    color .18s ease,
    border-color .18s ease,
    background .18s ease;
}

.quick-amounts button:not(:disabled):hover {
  transform: translateY(-2px);
  color: #dffbff;
  border-color: rgba(103,232,249,.18);
}

.quick-amounts button.active {
  color: #cffafe;
  background: rgba(6,182,212,.09);
  border-color: rgba(103,232,249,.24);
}

.calc-grid > div,
.order-data > div {
  position: relative;
  overflow: hidden;

  background:
    linear-gradient(145deg, rgba(255,255,255,.047), rgba(255,255,255,.018));

  transition:
    transform .2s ease,
    border-color .2s ease,
    background .2s ease;
}

.calc-grid > div::before,
.order-data > div::before {
  content: '';
  position: absolute;
  top: 0;
  left: 16%;
  right: 16%;
  height: 1px;

  background: linear-gradient(90deg, transparent, rgba(255,255,255,.16), transparent);
}

.calc-grid > div:hover,
.order-data > div:hover {
  transform: translateY(-2px);
  border-color: rgba(103,232,249,.15);
}

.calc-grid .active,
.order-data .highlight-data {
  background:
    radial-gradient(circle at 100% 0%, rgba(212,175,55,.12), transparent 42%),
    linear-gradient(145deg, rgba(42,32,14,.6), rgba(13,17,26,.86));
}

.principal-flow {
  background:
    linear-gradient(145deg, rgba(255,255,255,.035), rgba(255,255,255,.012));
}

.flow-track,
.progress-line,
.quota-line,
.process-track {
  position: relative;
}

.flow-track::after,
.progress-line::after,
.quota-line::after,
.process-track::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;

  background:
    repeating-linear-gradient(
      115deg,
      transparent 0 8px,
      rgba(255,255,255,.09) 9px 12px
    );
  background-size: 28px 100%;
  animation: strategyTrackFlow 2.2s linear infinite;
}

.flow-pool {
  background: linear-gradient(90deg, #0891b2, #6366f1);
}

.rule-note {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Trial panel */
.trial-product-card {
  background:
    radial-gradient(circle at 100% 0, rgba(124,58,237,.2), transparent 31%),
    radial-gradient(circle at 0% 100%, rgba(6,182,212,.14), transparent 38%),
    linear-gradient(145deg, rgba(13,23,45,.9), rgba(6,9,19,.98));

  border-color: rgba(103,232,249,.2);

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.06),
    0 20px 45px rgba(0,0,0,.23);
}

.trial-product-card::before {
  content: '';
  position: absolute;
  z-index: 0;
  left: -90px;
  top: -80px;
  width: 120px;
  height: 360px;
  transform: rotate(25deg);

  background: linear-gradient(90deg, transparent, rgba(103,232,249,.09), transparent);
  animation: strategyTrialBeam 9s ease-in-out infinite;
}

.trial-amount {
  background:
    linear-gradient(145deg, rgba(6,182,212,.11), rgba(124,58,237,.08));
  border-color: rgba(103,232,249,.18);
}

.trial-amount em,
.trial-feature-grid em {
  color: #67e8f9;
}

.quota-line div {
  background: linear-gradient(90deg, #0891b2, #6366f1);
  box-shadow: 0 0 15px rgba(34,211,238,.26);
}

/* Primary action */
.primary-btn {
  position: relative;
  overflow: hidden;

  box-shadow:
    0 17px 38px rgba(212,175,55,.22),
    inset 0 1px 0 rgba(255,255,255,.35);
}

.primary-btn::before {
  content: '';
  position: absolute;
  top: -26px;
  bottom: -26px;
  left: -58px;
  width: 38px;
  transform: skewX(-22deg);

  background: linear-gradient(90deg, transparent, rgba(255,255,255,.48), transparent);
  animation: strategyPrimaryShine 5.8s ease-in-out infinite;
}

.primary-btn.trial {
  background: linear-gradient(135deg, #0891b2 0%, #4f46e5 58%, #8b5cf6 100%);
  box-shadow:
    0 17px 38px rgba(79,70,229,.23),
    inset 0 1px 0 rgba(255,255,255,.18);
}

.primary-btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow:
    0 22px 46px rgba(212,175,55,.28),
    inset 0 1px 0 rgba(255,255,255,.4);
}

.primary-btn.trial:not(:disabled):hover {
  box-shadow:
    0 22px 46px rgba(79,70,229,.3),
    inset 0 1px 0 rgba(255,255,255,.22);
}

/* Portfolio summary */
.portfolio-summary {
  margin-top: 18px;

  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
}

.portfolio-summary > div {
  position: relative;
  overflow: hidden;
  min-width: 0;
  padding: 13px 9px;

  border-radius: 17px;
  border: 1px solid rgba(167,139,250,.1);

  background:
    linear-gradient(155deg, rgba(255,255,255,.047), rgba(255,255,255,.015));

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.045),
    0 12px 26px rgba(0,0,0,.16);
}

.portfolio-summary > div::before {
  content: '';
  position: absolute;
  left: 18%;
  right: 18%;
  top: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(103,232,249,.42), transparent);
}

.portfolio-summary small,
.portfolio-summary strong,
.portfolio-summary span {
  display: block;
}

.portfolio-summary small {
  overflow: hidden;
  color: #657594;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 6px;
  font-weight: 900;
  letter-spacing: .35px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.portfolio-summary strong {
  margin-top: 7px;
  color: #e7faff;
  font-size: 24px;
  line-height: 1;
  text-shadow: 0 0 18px rgba(103,232,249,.15);
}

.portfolio-summary span {
  margin-top: 6px;
  color: #8190ad;
  font-size: 8px;
}

/* Filter */
.order-filter {
  background:
    linear-gradient(145deg, rgba(255,255,255,.042), rgba(255,255,255,.014));
  border-color: rgba(167,139,250,.1);
}

.order-filter button {
  transition:
    color .18s ease,
    background .18s ease,
    transform .18s ease,
    box-shadow .18s ease;
}

.order-filter button:hover {
  color: #dce6f8;
}

.order-filter button.active {
  color: #fff;
  background:
    linear-gradient(135deg, rgba(6,182,212,.13), rgba(124,58,237,.12));
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.055),
    0 8px 18px rgba(0,0,0,.17);
}

.order-filter button.active span {
  color: #cffafe;
  background: rgba(6,182,212,.1);
}

/* Empty state */
.empty-box {
  position: relative;
  overflow: hidden;

  background:
    radial-gradient(circle at 50% 0%, rgba(124,58,237,.08), transparent 42%),
    rgba(255,255,255,.02);
  border-color: rgba(167,139,250,.12);
}

.empty-icon {
  color: #cffafe;
  background: linear-gradient(145deg, rgba(6,182,212,.11), rgba(124,58,237,.09));
  border-color: rgba(103,232,249,.15);
  box-shadow: 0 0 24px rgba(34,211,238,.07);
  animation: strategyEmptyFloat 3.8s ease-in-out infinite;
}

/* Order cards */
.order-card {
  position: relative;
  isolation: isolate;

  background:
    radial-gradient(circle at 100% 0%, rgba(212,175,55,.075), transparent 29%),
    linear-gradient(160deg, rgba(14,19,31,.98), rgba(4,8,14,.995));

  border-color: rgba(255,224,138,.12);

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.052),
    0 20px 46px rgba(0,0,0,.29);

  transition:
    transform .23s ease,
    border-color .23s ease,
    box-shadow .23s ease;
}

.order-card:hover {
  transform: translateY(-4px);
  border-color: rgba(103,232,249,.18);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.07),
    0 27px 58px rgba(0,0,0,.38),
    0 0 28px rgba(34,211,238,.045);
}

.order-card.trial-order {
  background:
    radial-gradient(circle at 100% 0%, rgba(124,58,237,.13), transparent 31%),
    radial-gradient(circle at 0% 100%, rgba(6,182,212,.07), transparent 34%),
    linear-gradient(160deg, rgba(12,15,32,.98), rgba(4,8,14,.995));

  border-color: rgba(167,139,250,.14);
}

.order-tech-grid {
  position: absolute;
  z-index: -2;
  inset: 0;
  pointer-events: none;
  opacity: .3;

  background-image:
    linear-gradient(rgba(103,232,249,.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(167,139,250,.022) 1px, transparent 1px);
  background-size: 22px 22px;
  mask-image: linear-gradient(135deg, transparent 8%, #000 65%, transparent);
}

.order-scan-line {
  position: absolute;
  z-index: -1;
  top: -1px;
  left: -90px;
  width: 90px;
  height: 1px;

  background: linear-gradient(90deg, transparent, #67e8f9, transparent);
  box-shadow: 0 0 10px rgba(103,232,249,.55);
  animation: strategyOrderScan 6.5s linear infinite;
}

.order-accent {
  background: linear-gradient(180deg, #ffe08a, rgba(212,175,55,.08));
  box-shadow: 0 0 12px rgba(212,175,55,.22);
}

.trial-order .order-accent {
  background: linear-gradient(180deg, #67e8f9, #8b5cf6, rgba(124,58,237,.06));
  box-shadow: 0 0 12px rgba(103,232,249,.2);
}

.order-type-badge.standard {
  color: #ffe8a6;
}

.order-type-badge.trial {
  color: #cffafe;
  background: rgba(6,182,212,.075);
  border-color: rgba(103,232,249,.15);
}

.order-rule-chip {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
}

.status i {
  animation: strategyPulseDot 1.7s ease-in-out infinite;
}

.trial-order .order-data .highlight-data {
  background:
    radial-gradient(circle at 100% 0%, rgba(6,182,212,.12), transparent 42%),
    linear-gradient(145deg, rgba(8,32,39,.68), rgba(11,13,25,.88));
  border-color: rgba(103,232,249,.16);
}

.trial-order .order-data em {
  color: #67e8f9;
}

.progress-line > div {
  background-size: 180% 100%;
  animation: strategyProgressGlow 4s linear infinite;
}

.progress-line.trial-progress > div {
  background: linear-gradient(90deg, #0891b2, #6366f1, #a78bfa);
  background-size: 180% 100%;
}

.order-actions button {
  position: relative;
  overflow: hidden;

  transition:
    transform .18s ease,
    border-color .18s ease,
    color .18s ease,
    box-shadow .18s ease;
}

.order-actions button:not(:disabled):hover {
  transform: translateY(-2px);
  border-color: rgba(103,232,249,.17);
  box-shadow: 0 10px 22px rgba(0,0,0,.2);
}

.order-actions button.gold {
  box-shadow: inset 0 1px 0 rgba(255,255,255,.3);
}

.order-actions button.trial-action.ready {
  background: linear-gradient(135deg, #0891b2, #4f46e5, #8b5cf6);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.18);
}

/* Process panel */
.order-process-panel {
  position: relative;
  overflow: hidden;

  background:
    radial-gradient(circle at 100% 0, rgba(6,182,212,.11), transparent 38%),
    linear-gradient(145deg, rgba(6,15,27,.9), rgba(4,8,15,.96));

  border-color: rgba(103,232,249,.14);
}

.order-process-panel::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100px;
  width: 100px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #67e8f9, transparent);
  animation: strategyProcessScan 4.5s linear infinite;
}

.process-plan {
  color: #cffafe;
  background: rgba(6,182,212,.07);
  border-color: rgba(103,232,249,.13);
}

.process-track > div {
  background: linear-gradient(90deg, #0891b2, #4f46e5, #8b5cf6);
}

.load-more-btn-dark {
  transition:
    transform .18s ease,
    border-color .18s ease,
    color .18s ease,
    background .18s ease;
}

.load-more-btn-dark:not(:disabled):hover {
  transform: translateY(-2px);
  color: #cffafe;
  border-color: rgba(103,232,249,.16);
  background: rgba(6,182,212,.055);
}

/* =========================================================
 * Animations
 * ========================================================= */

@keyframes strategyPageAmbient {
  0%, 100% {
    background-position: 0% 0%, center;
  }
  50% {
    background-position: 100% 100%, center;
  }
}

@keyframes strategyHeroEdge {
  from { background-position: 0% 50%; }
  to { background-position: 220% 50%; }
}

@keyframes strategyHeroGrid {
  from { background-position: 0 0; }
  to { background-position: 64px 64px; }
}

@keyframes strategyGlowPulse {
  0%, 100% {
    opacity: .5;
    transform: scale(.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@keyframes strategyHeroScan {
  0%, 16% {
    transform: rotate(22deg) translateX(-210px);
    opacity: 0;
  }
  31% {
    opacity: .42;
  }
  72% {
    transform: rotate(22deg) translateX(760px);
    opacity: .12;
  }
  100% {
    transform: rotate(22deg) translateX(760px);
    opacity: 0;
  }
}

@keyframes strategyDataStream {
  from { background-position: 0 0; }
  to { background-position: 0 84px; }
}

@keyframes strategyPulseDot {
  0%, 100% {
    opacity: .45;
    transform: scale(.82);
  }
  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

@keyframes strategyTitleGradient {
  from { background-position: 0% 50%; }
  to { background-position: 190% 50%; }
}

@keyframes strategyCardShine {
  0%, 20% {
    left: -44px;
    opacity: 0;
  }
  30% {
    opacity: 1;
  }
  47% {
    left: calc(100% + 44px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 44px);
    opacity: 0;
  }
}

@keyframes strategyOptionShine {
  0%, 20% {
    left: -52px;
    opacity: 0;
  }
  29% {
    opacity: 1;
  }
  46% {
    left: calc(100% + 52px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 52px);
    opacity: 0;
  }
}

@keyframes strategySelectedFlow {
  from { left: -80px; }
  to { left: calc(100% + 80px); }
}

@keyframes strategyTrackFlow {
  from { background-position: 0 0; }
  to { background-position: 28px 0; }
}

@keyframes strategyTrialBeam {
  0%, 18% {
    transform: rotate(25deg) translateX(-160px);
    opacity: 0;
  }
  34% {
    opacity: .7;
  }
  72% {
    transform: rotate(25deg) translateX(620px);
    opacity: .1;
  }
  100% {
    transform: rotate(25deg) translateX(620px);
    opacity: 0;
  }
}

@keyframes strategyPrimaryShine {
  0%, 23% {
    left: -58px;
    opacity: 0;
  }
  33% {
    opacity: 1;
  }
  51% {
    left: calc(100% + 58px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 58px);
    opacity: 0;
  }
}

@keyframes strategyEmptyFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

@keyframes strategyOrderScan {
  from { left: -90px; }
  to { left: calc(100% + 90px); }
}

@keyframes strategyProgressGlow {
  from { background-position: 0% 50%; }
  to { background-position: 180% 50%; }
}

@keyframes strategyProcessScan {
  from { left: -100px; }
  to { left: calc(100% + 100px); }
}

/* =========================================================
 * Responsive
 * ========================================================= */

@media (max-width: 420px) {
  .hero-premium {
    min-height: 610px;
    padding-top: 27px;
  }

  .hero-system-rail {
    max-width: 250px;
  }

  .hero-system-rail > span {
    min-height: 23px;
    padding: 0 8px;
    font-size: 6px;
  }

  .hero-premium h1 {
    margin-top: 19px;
    font-size: 36px;
  }

  .portfolio-summary {
    gap: 7px;
  }

  .portfolio-summary > div {
    padding: 11px 7px;
  }

  .portfolio-summary strong {
    font-size: 21px;
  }

  .portfolio-summary span {
    font-size: 7px;
  }

  .order-mode-card {
    min-height: 102px;
  }
}

@media (min-width: 768px) {
  .hero-premium {
    min-height: 670px;
  }

  .hero-system-rail {
    margin-top: 15px;
  }

  .hero-system-rail > span {
    min-height: 28px;
    padding: 0 11px;
    font-size: 8px;
  }

  .portfolio-summary {
    max-width: 680px;
    gap: 12px;
  }

  .portfolio-summary > div {
    padding: 16px 14px;
  }

  .portfolio-summary small {
    font-size: 8px;
  }

  .portfolio-summary strong {
    font-size: 29px;
  }

  .portfolio-summary span {
    font-size: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .invest-page::before,
  .hero-premium::after,
  .hero-grid-bg,
  .hero-glow-gold,
  .hero-glow-blue,
  .hero-scan-beam,
  .hero-data-stream,
  .hero-badge i,
  .hero-system-rail .rail-online i,
  .hero-premium h1 span,
  .hero-model > div::after,
  .order-mode-card::after,
  .selected-plan-bar::after,
  .flow-track::after,
  .progress-line::after,
  .quota-line::after,
  .process-track::after,
  .trial-product-card::before,
  .primary-btn::before,
  .empty-icon,
  .order-scan-line,
  .status i,
  .progress-line > div,
  .order-process-panel::after {
    animation: none !important;
  }
}

</style>
